import { RefObject } from "react";
import { EquipmentType, SchematicsModel, SREquipmentsDto } from "../store/model/calculations/schematics";
import { chartDataModel, DepletionCurveGraphData, Equipment, IPRplotData, WellSurveyData } from "../store/model/calculations/calculation";
import { MainReport } from "../store/model/calculations/MainReport";
import { RootState } from "../store/ConfigureStore";
import { toast } from "react-toastify";
import FileSaver from "file-saver";
import { CatalogType } from "../store/model/equipment-catalog/catalog";
import moment from "moment";
import { JetLiftMainReport } from "../store/model/jetlift/jetliftMainreport";

export interface DropdownOptions {
    id: string;
    name: string;
    series?: string;
    seriesType?: string;
};

export const getFirstLetters = (name: string) => {
    const words = name && name?.trim().split(" ");
    if (words && words?.length < 2) return "";
    return (
        words && words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase()
    );
};

export const getLocalTimeFormat = (date: string) => {
    const localDate = new Date(date);
    const day = localDate.getDate();
    const suffix = (day: number) => {
        if (day >= 11 && day <= 13) return "th";
        return ["th", "st", "nd", "rd"][day % 10] || "th";
    };
    const month = localDate.toLocaleString(undefined, { month: "short" });
    const year = localDate.getFullYear();
    const formattedDate = `${month} ${day}${suffix(day)}, ${year}`;
    const hours = localDate.getHours();
    const minutes = localDate.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedTime = `${(((hours + 11) % 12) + 1)
        .toString()
        .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    return `${formattedDate} ${formattedTime}`;
};

export const isDaysDifference = (dateString: string, days: number) => {
    const today = new Date();
    const itemDate = new Date(dateString);
    const daysDifferene = Math.ceil(
        (today.getTime() - itemDate.getTime()) / (1000 * 3600 * 24)
    );
    return daysDifferene <= days;
};

export const calculateTotalPages = (totalRecords: any, recordsPerPage: any) => {
    return Math.ceil(totalRecords / recordsPerPage);
};

export function formatDateDDMMYYYY(dateString?: string): string | undefined {
    if (!dateString) return dateString;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
}

export const getGreeting = (hour: number): string => {
    if (hour < 12) {
        return "Good morning";
    } else if (hour >= 12 && hour < 18) {
        return "Good afternoon";
    } else {
        return "Good evening";
    }
};

export const handleClickOutside = (
    ref: RefObject<HTMLElement>,
    callback: () => void
) => {
    const handleClick = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
            callback();
        }
    };

    document.addEventListener("mousedown", handleClick);
    return () => {
        document.removeEventListener("mousedown", handleClick);
    };
};

export const toNumber = (value: string | undefined): number => {
    const parsedValue = parseFloat(value || "");
    return isNaN(parsedValue) ? 0 : parsedValue;
};

export const transformObjectToDropdownOptions = (obj: any): DropdownOptions[] => {
    return Object.keys(obj).map((key) => ({
        id: obj[key],   // Use the value as the ID
        name: key,      // Use the key as the name
    }));
};

export const transformObjectToKeyValueArray = (obj: any) => {
    let array = [];
    array?.push({ label: "-Select-", value: "" });
    if (obj && Object?.keys(obj)?.length > 0) {
        Object?.keys(obj)?.forEach((key) => {
            array?.push({
                value: obj[key],
                label: key,
            });
        });
        return array?.length > 0 ? array : [];
    }
    return [];
};
export const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeout: NodeJS.Timeout | null;
    return (...args: any[]) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => func(...args), delay);
    };
};

export const formatTime = (date: Date | string): string => {
    var date1 = `${date}z`;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcDate = typeof date === "string" ? new Date(date1) : date1;
    const localDateStr = utcDate.toLocaleString("en-US", { timeZone: userTimeZone });
    const localDate = new Date(localDateStr);
    const now = new Date();
    const localNowStr = now.toLocaleString("en-US", { timeZone: userTimeZone });
    const localNow = new Date(localNowStr);
    const diff = Math.floor((localNow.getTime() - localDate.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    if (diff < 604800) {
        const days = Math.floor(diff / 86400);
        return `${days} day${days > 1 ? "s" : ""} ago`;
    }
    return localDate.toLocaleDateString("en-US");
};


export const downloadExcelFile = (data: string, catalogType: any) => {
    const blob = new Blob([data], { type: 'application/json' });
    FileSaver.saveAs(blob, getFileName(catalogType));
};

export const getFileName = (catalogType: any) => {
    const date = new Date();
    const formattedDate = moment(date).format("MM-DD-YYYY");
    if (catalogType === CatalogType?.DRCables) {
        return `Catalog_DR_Cables_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRGasHandlers) {
        return `Catalog_DR_GKXPumps_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRGasSeparators) {
        return `Catalog_DR_GasSeparators_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRMotorLeads) {
        return `Catalog_DR_MotorLeads_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRMotorSensors) {
        return `Catalog_DR_MotorSensors_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRMotors) {
        return `Catalog_DR_Motors_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRProtectors) {
        return `Catalog_DR_Protectors_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRPumps) {
        return `Catalog_DR_Pumps_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRSwitchBoard) {
        return `Catalog_DR_SwitchBoard_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.DRVSD) {
        return `Catalog_DR_VSD_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRAccAboveESP) {
        return `Catalog_SR_ACCAboveESP_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRAccBelowESP) {
        return `Catalog_SR_ACCBelowESP_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRBOH) {
        return `Catalog_SR_BOH_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRCables) {
        return `Catalog_SR_Cables_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRGasHandlers) {
        return `Catalog_SR_GasHandlers_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRGasSeparators) {
        return `Catalog_SR_GasSeparators_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRMLE) {
        return `Catalog_SR_MLE_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRMotors) {
        return `Catalog_SR_Motors_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRProtectors) {
        return `Catalog_SR_Protectors_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRPumps) {
        return `Catalog_SR_Pumps_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRTransformers) {
        return `Catalog_SR_Transformers_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.SRVSDs) {
        return `Catalog_SR_VSDs_${formattedDate}.xlsx`;
    } else if (catalogType === CatalogType?.HarmonicFilters) {
        return `Catalog_HarmonicFilters_${formattedDate}.xlsx`;
    }
};

export const mergeWithPrefixes = (response: any) => {
    if (Object?.keys(response)?.length > 0) {
        const prefixedAuditLog = Object?.fromEntries(
            Object?.entries(response.auditLog).map(([key, value]) => [`audit_${key}`, value])
        );

        const prefixedCatalogItem = Object?.fromEntries(
            Object?.entries(response?.catalogItem).map(([key, value]) => [`catalog_${key}`, value])
        );

        return [{ ...prefixedAuditLog, ...prefixedCatalogItem }];
    }
    return;
};

export const mappedExistedEquipments = (equipments: SREquipmentsDto[] | any[]) => {
    return equipments?.map(equipment => {
        const mappedEquipment: Equipment = {
            ...equipment,
            label: equipment?.equipmentName,
            value: equipment?.equipmentId,
            id: equipment.equipmentId,
            parentId: equipment.parentId,
            equipmentjson: equipment.equipments,
            equipmentId: equipment.equipmentId,
            length: equipment?.length,
            weight: equipment?.weight,
            pn: equipment?.pn,
            diameter: equipment?.diameter,
            setDepth: equipment?.setDepth,
            equipmentName: equipment?.equipmentName,
            pN_Description: equipment?.equipmentName,
            text: equipment?.equipmentName,
            series: equipment?.series,
            configuration: equipment?.configuration,
            equipments: equipment?.equipments,
            shaftType: equipment?.shaftType,
            noStgs: equipment?.noStgs,
            dimensions: equipment?.diameter,
            totalLength: equipment?.length,
            nominalWeight: equipment?.weight,
        };
        return mappedEquipment;
    });
};

export const mapToSREquipmentsDto = (
    equipment: Equipment,
    projectId: string,
    equipmentType: number,
    index: number,
    isUpperProtector: boolean = false
) => {
    if (!equipment) return {};

    const baseDto = {
        projectId,
        displayOrder: index,
        equipmentType,
        parentId: equipment.parentId,
        configuration: equipment.configuration,
        length: equipment.length || 0,
        weight: equipment.weight || 0,
        pn: equipment.pn ?? equipment?.oracle_Item_Number,
        diameter: equipment.diameter ?? equipment.od ?? equipment.series,
        setDepth: equipment.setDepth,
        equipmentName: equipment.pN_Description ?? equipment.text ?? equipment.label ?? equipment.transformers ?? "",
    };

    if (equipment.id && equipmentType === EquipmentType.SRCables) {
        return {
            projectId: projectId,
            equipmentId: equipment.id,
            displayOrder: index,
            equipmentType: equipmentType,
            parentId: equipment?.parentId,
            equipments: equipment?.equipmentjson,
            length: equipment?.totalLength,
            weight: equipment?.nominalWeight,
            pn: equipment?.pn,
            diameter: equipment?.dimensions,
            setDepth: equipment?.setDepth,
            equipmentName: equipment?.pN_Description ? equipment.pN_Description : equipment.text
        }
    }

    if (equipment.id && equipmentType === EquipmentType.SRVSDs) {
        return {
            ...baseDto,
            id: equipment?.id,
            equipmentId: equipment?.id,
        }
    }

    if (equipment.id && equipmentType === EquipmentType.SRSensor) {
        return { ...baseDto, equipments: equipment.equipments, id: equipment.id };
    }

    if (
        equipment.id &&
        [EquipmentType.SRPumps, EquipmentType.SRChargePump1, EquipmentType.SRChargePump2, EquipmentType.SRChargePump3].includes(equipmentType)
    ) {
        return { ...baseDto, equipmentId: equipment.id, equipments: equipment.equipmentjson, noStgs: equipment.noStgs, shaftType: equipment.shaftType };
    }

    if (equipment.id && equipmentType === EquipmentType.SRProtectors) {
        return {
            ...baseDto,
            equipmentId: equipment.id,
            series: equipment.series,
            displayOrder: isUpperProtector ? 0 : 1,
            equipments: isUpperProtector ? "1" : "0",
        };
    }

    if (equipment.id) {
        return { ...baseDto, equipmentId: equipment.id, equipments: equipment.equipmentjson, shaftType: equipment.shaftType };
    }

    return {};
};


export const convertObjToDropdown = (obj: any): any[] => {
    return Object.keys(obj).map((key) => ({
        id: obj[key].id,
        label: key,
        series: obj[key].series,
        seriesType: obj[key].seriesType
    }));
};


export const mappedMotorLockEquipments = (equipments: SREquipmentsDto[] | any[]) => {
    return equipments?.map(equipment => {
        const mappedEquipment: Equipment = {
            ...equipment,
            label: equipment?.equipmentName,
            mle: equipment?.equipmentName,
            value: equipment?.equipmentId,
            id: equipment.equipmentId,
            parentId: equipment.parentId,
            equipmentId: equipment.equipmentId,
            totalLength: equipment?.length,
            nominalWeight: equipment?.weight,
            pn: equipment?.pn,
            dimension: equipment?.diameter,
            setDepth: equipment?.setDepth,
            pN_Description: equipment?.equipmentName,
            configuration: equipment?.configuration,
            noStgs: equipment?.noStgs,
        };
        return mappedEquipment;
    });
};
export const isEmptyObject = (obj: any) => {
    const updatedObj = obj && obj?.map((equipment: any) => {
        if (equipment?.id) {
            return equipment;
        }
        return null;
    });
    return updatedObj;
};

export const createChart1 = (chData: any) => {
    const scale = Math?.ceil(parseFloat(chData?.[3]?.[4]) / 1000);
    const xAxisMax = scale * (1000 * ((1 / scale) * (scale + 1)));

    return {
        seriesDefaults: {
            type: "scatterLine",
        },
        legend: {
            position: "bottom",
        },
        chartArea: {
            width: 900,
            height: 700,
        },
        xAxis: {
            min: 0,
            title: {
                text: `Capacity (${chData?.[3]?.[0]})`,
            },
            axisCrossingValue: [0, xAxisMax, xAxisMax],
            majorGridLines: {
                width: 0.8,
                color: "#A0A0A0",
            },
            minorGridLines: {
                width: 0.3,
                color: "#A0A0A0",
                visible: true,
            },
        },
        yAxis: [
            {
                name: "Head",
                title: {
                    text: `Head (${chData?.[3]?.[1]})`,
                },
                min: 0,
                majorUnit: parseFloat(chData?.[3]?.[6]),
                color: "#0000FF",
                minorGridLines: {
                    width: 0.3,
                    color: "#A0A0A0",
                    visible: true,
                },
                majorGridLines: {
                    width: 0.8,
                    color: "#A0A0A0",
                },
            },
            {
                name: "Power",
                title: {
                    text: `Power (${chData?.[3]?.[2]})`,
                },
                min: 0,
                max: parseFloat(chData?.[3]?.[9]),
                majorUnit: parseFloat(chData?.[3]?.[8]),
                color: "#FF0000",
            },
            {
                name: "Efficiency",
                title: {
                    text: "Efficiency (%)",
                },
                min: 0,
                majorUnit: 10,
                color: "#006633",
            },
        ],
        series: [
            {
                name: "Head",
                type: "scatterLine",
                data: chData?.[0],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false,
                },
                width: 4.0,
                tooltip: {
                    visible: true,
                    format: "{0} x {1} ({2:C})",
                },
            },
            {
                name: "Power",
                type: "scatterLine",
                data: chData?.[1],
                color: "#FF0000",
                yAxis: "Power",
                markers: {
                    visible: false,
                },
                width: 4.0,
                style: "smooth",
            },
            {
                name: "Efficiency",
                type: "scatterLine",
                data: chData?.[2],
                color: "#006633",
                yAxis: "Efficiency",
                markers: {
                    visible: false,
                },
                width: 4.0,
                style: "smooth",
            },
            {
                type: "scatterLine",
                data: chData?.[4],
                color: "#737373",
                yAxis: "Head",
                markers: {
                    visible: false,
                },
                width: 2.0,
                dashType: "longDash",
                labels: {
                    visible: true,
                    font: "10px calibri",
                    position: "bottom",
                    rotation: 270,
                    padding: {
                        bottom: 20,
                    },
                },
                visibleInLegend: true,
            },
            {
                name: "BEPFlow",
                type: "scatterLine",
                data: chData?.[5],
                color: "#006633",
                yAxis: "Head",
                markers: {
                    visible: false,
                },
                width: 2.0,
                dashType: "longDash",
                labels: {
                    visible: true,
                    font: "10px calibri",
                    position: "bottom",
                    rotation: 270,
                    padding: {
                        bottom: 20,
                    },
                },
                visibleInLegend: false,
            },
            {
                name: "MaxOperatingFlow",
                type: "scatterLine",
                data: chData?.[6],
                color: "#737373",
                yAxis: "Head",
                markers: {
                    visible: false,
                },
                width: 2.0,
                dashType: "longDash",
                labels: {
                    visible: true,
                    font: "10px calibri",
                    position: "bottom",
                    rotation: 270,
                    padding: {
                        bottom: 20,
                    },
                },
                visibleInLegend: false,
            },
            {
                name: "Target",
                type: "scatter",
                data: chData?.[7],
                color: "#4d4d4d",
                yAxis: "Head",
                markers: {
                    visible: true,
                    type: "square",
                    background: "black",
                    size: 10,
                },
                width: 2.0,
                labels: {
                    visible: true,
                    background: "#bfbfbf",
                    border: {
                        width: 1,
                        color: "black",
                    },
                    font: "11px calibri",
                },
                tooltip: {
                    visible: true,
                },
                visibleInLegend: false,
            },
            {
                name: "TargetH",
                type: "scatterLine",
                data: chData?.[8],
                color: "#bfbfbf",
                yAxis: "Head",
                markers: {
                    visible: false,
                },
                width: 2.0,
                dashType: "dot",
                tooltip: {
                    visible: false,
                },
                visibleInLegend: false,
            },
            {
                name: "TargetV",
                type: "scatterLine",
                data: chData?.[9],
                color: "#bfbfbf",
                yAxis: "Head",
                markers: {
                    visible: false,
                },
                width: 2.0,
                dashType: "dot",
                tooltip: {
                    visible: false,
                },
                visibleInLegend: false,
            },
        ],
    };
};

export const createChart2 = (chData: any) => {
    return {
        seriesDefaults: {
            type: "scatterLine",
        },
        legend: {
            position: "bottom"
        },
        chartArea: {
            width: 900,
            height: 600
        },
        series: [
            {
                name: "45 Hz",
                data: chData?.[0],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("# = testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "50 Hz",
                data: chData?.[1],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "55 Hz",
                data: chData?.[2],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "60 Hz",
                data: chData?.[3],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "65 Hz",
                data: chData?.[4],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "70 Hz",
                data: chData?.[5],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "75 Hz",
                data: chData?.[6],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "Min",
                data: chData?.[7],
                color: "#737373",// "#ffcc00",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "Max",
                data: chData?.[8],
                color: "#737373",// "#ffcc00",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "BEP",
                data: chData?.[9],
                color: "#0000FF",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "TargetFlow",
                data: chData?.[10],
                color: "#006633",// "#cc0066",
                yAxis: "Head",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "dot",// "longDash",
                visibleInLegend: false,

            },
            {
                name: "Target",
                type: "scatter",
                data: chData?.[12],
                color: "#4d4d4d",
                yAxis: "Head",
                markers: {
                    visible: true,
                    type: "square",
                    background: "black",
                    size: 10
                },
                width: 2.0,
                labels: {
                    visible: true,
                    background: "#bfbfbf",
                    border: {
                        width: 1,
                        color: "black",

                    },
                    format: "Target",
                    font: "11px calibri"
                },
                tooltip: {
                    visible: true
                },
                visibleInLegend: false,
            },
        ],
        xAxis: {
            min: 0,
            max: parseFloat(chData?.[11]?.[3]),
            majorUnit: parseFloat(chData?.[11]?.[2]),
            title: {
                text: "Capacity (" + chData?.[11]?.[0] + ")"
            },
            axisCrossingValue: [0, parseFloat(chData?.[3]?.[4]) + 5, parseFloat(chData[3][4])],
            majorGridLines: {
                width: 0.8,
                color: "#A0A0A0",
            },
            minorGridLines: {
                width: 0.3,
                color: "#A0A0A0",
                visible: true
            }
        },
        yAxis: [
            {
                name: "Head",
                title: {
                    text: "Head (" + chData?.[11]?.[1] + ")"

                },
                min: 0,
                max: parseFloat(chData?.[11]?.[5]),
                majorUnit: parseFloat(chData?.[11]?.[4]),
                color: "#0000FF",
                minorGridLines: {
                    width: 0.3,
                    color: "#A0A0A0",
                    visible: true
                },
                majorGridLines: {
                    width: 0.8,
                    color: "#A0A0A0",
                },
            }
        ],
    }
};

export const createChart3 = (chData: any) => {
    return {
        seriesDefaults: {
            type: "scatterLine",
        },

        legend: {
            position: "bottom"
        },
        chartArea: {
            width: 900,
            height: 600
        },
        series: [
            {
                name: "45 Hz",
                data: chData?.[0],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplate("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "50 Hz",
                data: chData?.[1],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "55 Hz",
                data: chData?.[2],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "60 Hz",
                data: chData?.[3],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "65 Hz",
                data: chData?.[4],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "70 Hz",
                data: chData?.[5],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "75 Hz",
                data: chData?.[6],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 4.0,
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        bottom: 30
                    },
                    background: "transparent"
                }
            },
            {
                name: "Min",
                data: chData?.[7],
                color: "#737373",// "#ffcc00",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "Max",
                data: chData?.[8],
                color: "#737373",// "#ffcc00",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "BEP",
                data: chData?.[9],
                color: "#FF0000",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "longDash",
                labels: {
                    visible: false,
                    // format: this.testTemplateTest("#=testTemplateTest(series)#", chData),
                    position: "right",
                    margin: {
                        top: 40
                    }
                },
                visibleInLegend: false,
            },
            {
                name: "TargetFlow",
                data: chData?.[10],
                color: "#006633",// "#cc0066",
                yAxis: "Power",
                style: "smooth",
                markers: {
                    visible: false
                },
                width: 3.0,
                dashType: "dot",// "longDash"
                visibleInLegend: false,

            },
            {
                name: "Target",
                type: "scatter",
                data: chData?.[12],
                color: "blue",
                yAxis: "Power",
                markers: {
                    visible: true,
                    type: "square",
                    background: "blue",
                    size: 10
                },
                width: 2.0,
                labels: {
                    visible: true,
                    background: "#bfbfbf",
                    border: {
                        width: 1,
                        color: "black",

                    },
                    format: "Target",
                    font: "11px calibri"
                },
                tooltip: {
                    visible: true
                },
                visibleInLegend: false,
            },
        ],
        xAxis: {
            min: 0,
            max: parseFloat(chData?.[11]?.[3]),
            majorUnit: parseFloat(chData?.[11]?.[2]),
            title: {
                text: "Capacity (" + chData?.[11]?.[0] + ")"
            },
            majorGridLines: {
                width: 0.8,
                color: "#A0A0A0",
            },
            minorGridLines: {
                width: 0.3,
                color: "#A0A0A0",
                visible: true
            }
        },
        yAxis: [
            {
                name: "Power",
                title: {
                    text: "Power (" + chData?.[11]?.[1] + ")"

                },
                min: 0,
                max: parseFloat(chData?.[11]?.[5]),
                majorUnit: parseFloat(chData?.[11]?.[4]),
                color: "#FF0000",
                minorGridLines: {
                    width: 0.3,
                    color: "#A0A0A0",
                    visible: true
                },
                majorGridLines: {
                    width: 0.8,
                    color: "#A0A0A0",
                },
            }
        ],
    }
};


export const getMainReportDataFromUtility = (sateData: RootState) => {
    const { tab1Input } = sateData?.tubing;
    const { tab4Input } = sateData?.projectTab;
    const { tab3Input, region, regionList } = sateData?.fluid;
    const selectedRegion: any = regionList?.find((x: any) => x?.id === region);
    const { tab2Input, inflowOutput } = sateData?.wellInput;
    const projectDetails: any = sateData?.projectTab?.tab4Input;
    const { basicTDHCalculation } = sateData?.basicCalculations;
    const { GasCalculationOutPut, } = sateData?.gasCalculations;
    const gasFinalData = sateData?.gasCalculations?.GasFinalCalculatedData;
    const pumpInputData = sateData?.pump?.PumpInput;
    const { BasicGasCalculation, chargePumpName, chargePump2Name, chargePump3Name } = sateData?.gasCalculations;
    const pumpInputCalculated = sateData?.pump?.PumpInputCalculated;
    const surfaceVoltage = sateData?.cable?.cableSurfaceVoltage;
    const maxvoltage = sateData?.calculations?.motorFrequencyDataForSchematics;
    const surfaceEquipment = sateData?.surfaceEquipment;
    const selectedVSD = sateData?.tubing?.tab1Input?.hiddenSurfVSD;
    const wellList = sateData?.cable?.wellCableList;
    const selectedCable = Object.entries(wellList).find(([key, value]) => (value as any).id === tab1Input?.hdnCable_CableType);
    const selectedPumpData = sateData?.pump.SelectedPump;
    const VSDList = sateData?.surfaceEquipment?.VSDList;
    const SelectedVSDData = VSDList.find(x => x.vsD_ID === selectedVSD?.toString());
    const selectedGasSeperator = BasicGasCalculation?.sepSelcted;
    let selectedGasSeperatorData = "";
    if (Array.isArray(BasicGasCalculation?.separatorList)) {
        selectedGasSeperatorData = BasicGasCalculation?.separatorList?.find((k: any) => k?.id === selectedGasSeperator?.toString())?.label || "";
    } else {
        selectedGasSeperatorData = Object.keys(BasicGasCalculation?.separatorList ?? {}).find((k: any) => BasicGasCalculation?.separatorList[k].id === selectedGasSeperator?.toString()) || "";
    }
    const chargePumpGKX = BasicGasCalculation?.chargePumpGKX;
    const SelectedchargePumpGKX = Array.isArray(BasicGasCalculation?.chargePumpGKXList)
        ? BasicGasCalculation?.chargePumpGKXList.find(
            (x) => x?.value === chargePumpGKX?.toString()
        )
        : null;
    const selectedchargePump1 = chargePumpName;
    const selectedchargePump2 = chargePump2Name;
    const selectedchargePump3 = chargePump3Name;
    const depletionData = sateData?.basicCalculations?.depletionCurve;
    const { OilContractionList, selectedLowerProtector, selectedUpperProtector, thrustPercentage, shaftPercentate } = sateData?.protector;
    const motor = sateData?.motor;
    const { showWellDataInMainReport } = sateData?.wellSurvey;
    const { Chart1Data, Chart2Data, Chart3Data } = sateData?.pump;
    const { wellInflowData, iprCurve, showIPRPlotInMainReport, showDepletionPlotInMainReport } = sateData?.basicCalculations;
    const WellServeyNotes = sateData?.wellSurvey?.wellSurvey?.notes;
    const depthOfMaxDLS = sateData?.wellSurvey?.DepthOfMaxDLS ?? 0;
    const MaxDLSAboveKOP = sateData?.wellSurvey?.MaxDLSAboveKOP ?? 0;
    const tailPipeTZ = sateData?.wellSurvey?.TailPipeInsideTZ ?? 0;
    const wellServey = sateData?.wellSurvey;
    const wellSurveyChart1 = sateData?.wellSurvey?.wellSurveyData;
    const pumpData = sateData?.pump;
    const PumPIteration = sateData?.pump?.PumpIteration;
    const { MotorInput, motor_HPInput, SelectedMotor, ESPEquipments } = sateData?.motor;
    const { fluidVelocity, MotorperformanceList, Equipments } = sateData?.motor;
    const { CP1CalculationData, CP2CalculationData, CP3CalculationData, GKXCalculationData } = sateData?.gasHandler;
    const basicElectrictyCost = sateData?.surfaceEquipment?.BasicElectrictyCost;
    const harmonicFilter = sateData?.surfaceEquipment?.HarmonicFilter;
    const { SelectedVSD, SelectedTransformer } = sateData?.surfaceEquipment;
    const selectedSwitchBoard = surfaceEquipment?.SelectedSwitchBoard ?? '';
    const { cableAmpacity, cableConductorTempDesign, cableMaxCondutorTemp, cableTotalLength, cableTotalVoltageLoss, cableVDrop, cableVoltageLoss1000, cableClearance, pumpMotorLeadChart, sealMotorLeadChart, motorChart, tubingCableChart } = sateData?.cable;
    const cable = sateData?.cable;
    const kop = sateData?.wellSurvey?.KOPOverride;
    const isInValid = (value: string) => {
        return value === undefined || value === null || value === "" || value === "0";
    };
    const schematicsData = sateData?.calculations?.schematicData;
    const ischecked = schematicsData?.miscellaneous?.showSchematicData ?? false;
    const totalSystemTDH = sateData?.summary?.totalSystemTDH;
    const EquipmentsBelowESP = sateData?.schematic;
    const caseModifiedDate = sateData?.calculations?.caseModifiedDate;
    const pumpIterationLast = Array.isArray(PumPIteration) && PumPIteration?.length
        ? PumPIteration[PumPIteration.length - 1]
        : null;


    const {
        accountManagerList,
        accountManager,
        caseName
    } = sateData?.createProject

    const casingODList = tab1Input.casingODList.find(
        (item: { value: number }) => +item.value === +tab1Input.casingOD
    )?.label || null;

    const casingWeightList = tab1Input.casingWeightList.find(
        (item: { value: number }) => +item.value === +tab1Input.casingWeight
    )?.label || null;

    const tubingODList = tab1Input.tubingODList.find(
        (item: { value: number }) => +item.value === +tab1Input.tubingOD
    )?.label || null;

    const tubingWeightList = tab1Input.tubingWeightList.find(
        (item: { value: number }) => +item.value === +tab1Input.tubingWeight
    )?.label || null;
    let report = new MainReport();
    report.Case_ModifiedDate = caseModifiedDate ?? '';

    report.CaseName = caseName ?? '';
    report.CustomerName = projectDetails?.companyName;
    report.WellName = projectDetails?.wellName;
    report.ProjectName = projectDetails?.projectName;

    const dalRepr = tab4Input?.dalRepr ?? '';
    if (dalRepr && dalRepr.includes('@')) {
        const foundManager = Array.isArray(accountManagerList)
            ? accountManagerList.find((manager: any) => manager?.value === dalRepr)
            : null;
        report.AccountManager = foundManager?.label ?? dalRepr;
    } else {
        report.AccountManager = dalRepr;
    }

    report.CustomerRep = projectDetails?.customerContact?.toString() ?? '';
    report.WellApiNumber = projectDetails?.wellApiNo?.toString() ?? '';
    report.PumpSettingMD = tab2Input?.pumpSetting ? tab2Input?.pumpSetting?.toString() : '';
    report.PumpSettingMD_Unit = tab2Input?.actNamePumpSettingMeasured?.toString() ?? '';
    report.PumpSettingVD = tab2Input?.tvd ? tab2Input?.tvd?.toString() : '';

    report.PumpSettingVD_Unit = tab2Input?.actNamePumpSettingVertical?.toString() ?? '';
    report.Datum = tab2Input?.perforation ? tab2Input?.perforation?.toString() : '';
    report.Datum_Unit = tab2Input?.actNamePerforation;
    report.ProductionFL = basicTDHCalculation?.productionLevel;
    report.ProductionFL_Unit = basicTDHCalculation?.actNameBasic_ProductionLevel;
    report.Flop = tab2Input?.flop?.toString() ?? '';
    report.Flop_Unit = tab2Input?.actNameFLOP;
    report.PIP = GasCalculationOutPut?.Intake_Pressure?.toString() ?? '';
    report.Pump_PIP_Unit = basicTDHCalculation?.actNameGas_TotalPressure;
    report.TargetFlowRate = gasFinalData?.preTFV;
    report.TargetFlowRate_Unit = pumpInputData?.actNamePump_DesiredFlowRate;
    report.ProductionRate = tab2Input?.productionRate ? tab2Input?.productionRate?.toString() : '';
    report.ProductionRate_Unit = tab2Input?.actNameProductionRate;
    report.BOPD = tab3Input?.bopd?.toString() ?? '';
    report.BOPD_Unit = tab3Input?.actNameBOPD;
    report.BWPD = tab3Input?.bwpd?.toString() ?? ''
    report.BWPD_Unit = tab3Input?.actNameBWPD;
    report.GasProduction = tab3Input?.gasProduction ? tab3Input?.gasProduction?.toString() : '';

    report.GasProduction_Unit = tab3Input?.actNameGasProduction;
    report.OperatingFrequency = tab3Input?.operatngFreq?.toString() ?? '';
    report.OilGravity = tab3Input?.spGrOil?.toString() ?? ''
    report.OilApi = tab3Input?.oilAPI ? tab3Input?.oilAPI?.toString() : ''
    report.WaterGravity = tab3Input?.spGrWater ? tab3Input?.spGrWater?.toString() : '';
    report.WaterCut = tab3Input?.waterCut ? tab3Input?.waterCut?.toString() : ''
    report.BottomHoleTemprature = tab3Input?.bottomHoleTemp ? tab3Input?.bottomHoleTemp?.toString() : ''
    report.BottomHoleTemprature_Unit = tab3Input?.actNameBottomHoleTemp
    report.SurfaceFluidTemprature = tab3Input?.surfaceTemperature ? tab3Input?.surfaceTemperature?.toString() : ''
    report.SurfaceFluidTemprature_Unit = tab3Input?.actNameSurfaceTemp
    report.GasGravity = tab3Input?.spGrGas?.toString() ?? ''
    report.BubblePoint = tab3Input?.bubblePoint ? tab3Input?.bubblePoint?.toString() : ''
    report.BubblePoint_Unit = tab3Input?.actNameBubblePoint
    report.GOR = tab3Input?.gor?.toString() ?? ''
    report.GOR_Unit = tab3Input?.actNameGOR
    report.GLR = tab3Input?.glr?.toString() ?? ''
    report.GLR_Unit = tab3Input?.actNameGLR
    report.TotalVolumeIntake = GasCalculationOutPut?.Intake_TotalFluidVolume?.toString() ?? gasFinalData?.preTFV
    report.GasTFVUnit = pumpInputData?.actNamePump_DesiredFlowRate
    report.OilRate = GasCalculationOutPut?.Intake_VolumeOfOil?.toString() ?? gasFinalData?.VolumeOfOil
    report.OilRate_Unit = BasicGasCalculation?.actNameGas_OilVolume
    report.WaterRate = GasCalculationOutPut?.Intake_VolumeOfWater?.toString() ?? gasFinalData?.VolumeOfWater
    report.WaterRate_Unit = BasicGasCalculation?.actNameGas_WaterVolume
    report.GasRate = GasCalculationOutPut?.Intake_VolumeOfGas?.toString() ?? gasFinalData?.GasVolm
    report.GasRate_Unit = BasicGasCalculation?.actNameGas_GasVolume
    report.FreeGasIntakePercent = GasCalculationOutPut?.Intake_FreeGas?.toString() ?? ''
    report.PumpFreeGasPercent = gasFinalData?.FreeGasGvf
    report.PumpTDH = pumpInputCalculated?.pmpFinalTDH?.toString() ?? ''
    report.Pump_RequiredTDH = pumpInputCalculated?.pmpFinalTDH?.toString() ?? ''
    report.Pump_RequiredTDH_Unit = pumpInputData?.actNamePump_RequiredTDH
    report.CompositeSG = tab3Input?.combinedSpGr?.toString() ?? ''
    report.LiquidPhaseSG = tab3Input?.liquidPhaseCombinedSpGr?.toString() ?? ''

    report.Pump_TotalPower = pumpInputCalculated?.hPOPPump?.toString() ?? ''

    if (tab1Input?.unitSys === "0") {
        report.CasingOD = tab1Input?.casingOD?.toString() ?? ''
        report.TubingOD = tab1Input?.tubingOD?.toString() ?? ''
        report.LinerOD = tab1Input?.linerOD?.toString() ?? ''
    }
    else {
        report.CasingOD = tab1Input?.casingOD ? (tab1Input?.casingOD * 25.4).toFixed(2).toString() : ''
        report.TubingOD = tab1Input?.tubingOD ? (tab1Input?.tubingOD * 25.4).toFixed(2).toString() : ''
        report.LinerOD = tab1Input?.linerOD ? (tab1Input?.linerOD * 25.4).toFixed(2).toString() : ''
    }
    report.WellSurveyCasingSize = casingODList || '' + ' ' + casingWeightList || '';
    report.WellSurveyTubingSize = tubingODList || '' + ' ' + tubingWeightList || '';
    report.FormationValue = tab4Input?.formation ?? '';
    report.CasingOD_Unit = tab1Input?.actNameCasingOD ? tab1Input?.actNameCasingOD : '';
    report.CasingWeight = tab1Input?.casingWeight?.toString() ?? ''
    report.CasingWeight_Unit = tab1Input?.actNameCasingWeight
    report.Casing = report.CasingOD + ' ' + report.CasingOD_Unit + ' - ' + report.CasingWeight + ' ' + report.CasingWeight_Unit
    report.CasingID = tab1Input?.casingID
    report.SurfaceCableLength = tab1Input?.hdnCable_SurfCabl?.toString() ?? ''
    report.SurfaceCableLength_Unit = tab1Input?.actNameCable_SurfaceCableLength?.toString() ?? ''
    report.CasingID_Unit = tab1Input?.actNameCasingID
    report.Casing_Drift_ID = tab1Input?.casingDriftID
    report.CasingDriftID_Unit = tab1Input?.actNameCasingID
    report.TubingOD_Unit = tab1Input?.actNameTubingOD
    report.TubingWeight = tab1Input?.tubingWeight?.toString() ?? ''
    report.TubingWeight_Unit = tab1Input?.actNameTubingWeight
    report.Tubing = report.TubingOD + ' ' + report.TubingOD_Unit + ' - ' + report.TubingWeight + ' ' + report.TubingWeight_Unit
    report.TubingID = tab1Input?.tubingID
    report.TubingID_Unit = tab1Input?.actNameTubingID
    //report.TubingType = tab1Input?.tubingTypeUpset

    if (tab1Input?.tubingTypeUpset == 0)
        report.TubingType = "Non-Upset"
    else
        report.TubingType = "Upset"

    report.LinerOD_Unit = report.LinerOD === '' ? '' : tab1Input?.actNameLinerOD
    report.LinerWeight = tab1Input?.linerWeight?.toString() ?? ''
    report.LinerWeight_Unit = report.LinerWeight === '' ? '' : tab1Input?.actNameLinerWeight
    report.Liner = report.LinerOD + ' ' + report.LinerOD_Unit + ' - ' + report.LinerWeight + ' ' + report.LinerWeight_Unit
    report.LinerID = isInValid(tab1Input?.linerID) ? '' : tab1Input?.linerID
    report.LinerID_Unit = report.LinerID === '' ? '' : tab1Input?.actNameLinerID
    report.CasingPressure = tab2Input?.casingPressure?.toString() ?? '0'
    report.CasingPressure_Unit = report.CasingPressure === '' ? '' : tab2Input?.actNameCasingPressure
    report.TubingPressure = tab2Input?.tubingPressure ? tab2Input.tubingPressure.toString() : '';
    report.TubingPressure_Unit = report.TubingPressure === '' ? '' : tab2Input?.actNameTubingPressure
    report.Surface_SurfaceVoltage = surfaceVoltage?.toString() ?? ''
    report.Surface_SurfaceVoltage_Unit = tab1Input?.actNameCable_SurfaceVoltage?.toString() ?? ''
    report.Surface_DriveAmps = surfaceEquipment.driveAmps?.toString() ?? ''
    report.Surface_DriveAmps_Unit = tab1Input?.actNameSurface_DriveAmps
    report.Surface_KVARequired = surfaceEquipment?.KVA?.toString() ?? ''
    report.Surface_KVARequired_Unit = tab1Input?.actNameSurface_KVA?.toString() ?? ''
    report.Surface_KWRequired = surfaceEquipment.KW?.toString() ?? ''
    report.Surface_KWRequired_Unit = 'kW'
    report.Surface_SupplyVoltage = tab1Input?.hdnSurface_SuplVlt?.toString()
    report.Surface_SupplyVoltage_Unit = tab1Input?.actNameSurface_SupplyVoltage?.toString() ?? ''
    report.Controller = SelectedVSDData?.descriptionField ?? ''
    report.Protector_BearingLoad = thrustPercentage?.toString() ?? ''
    report.Protector_ShaftLoad = shaftPercentate.toString() ?? ''
    report.Cable = isInValid((selectedCable?.[1] as { label?: string })?.label ?? '') ? '' : (selectedCable?.[1] as { label?: string })?.label ?? ''
    report.Pump = selectedPumpData?.pumpName ?? '';
    report.Sensor = tab1Input?.hdnMotr_Sensr ?? '';

    report.PumpNoOfStages = pumpInputData?.pump_DesrdStges?.toString() ?? ''
    report.Gas_GasSeparator = selectedGasSeperatorData ? selectedGasSeperatorData : ''
    report.GasSep_NoOfSeparator = BasicGasCalculation?.noOfGasSeps ?? '';
    report.GKX = SelectedchargePumpGKX && SelectedchargePumpGKX?.value !== "" ? SelectedchargePumpGKX?.label : '';
    report.IsGKXVisible = !isInValid(report.GKX ?? '')
    report.Gas_ChargePump1 = selectedchargePump1 ?? ''
    report.Gas_ChargePump1_Stages = BasicGasCalculation?.chargePumpDesrdStge ? BasicGasCalculation?.chargePumpDesrdStge.toString() : ''
    report.Gas_ChargePump2 = selectedchargePump2 ?? ''
    report.Gas_ChargePump2_Stages = BasicGasCalculation?.chargePumpDesrdStge2 ? BasicGasCalculation?.chargePumpDesrdStge2.toString() : ''
    report.Gas_ChargePump3 = selectedchargePump3 ?? ''
    report.Gas_ChargePump3_Stages = BasicGasCalculation?.chargePumpDesrdStge3 ? BasicGasCalculation?.chargePumpDesrdStge3.toString() : ''
    report.IsGasChargePumpsVisible = !isInValid(report?.Gas_ChargePump1 ?? '')
    report.IsGasChargePumps2Visible = !isInValid(report?.Gas_ChargePump2)
    report.IsGasChargePumps3Visible = !isInValid(report?.Gas_ChargePump3)
    report.UpperProtector = selectedUpperProtector?.name ?? ''
    report.LowerProtector = selectedLowerProtector?.name ?? ''
    report.Motor = motor.SelectedMotor?.name
    report.MotorPower = motor?.SelectedMotor?.baseHP?.toString() ?? ''
    report.MotorPowerUnit = tab1Input?.actNameMotor_TPower || 'hp'
    report.MotorVoltage = motor?.SelectedMotor?.voltage?.toString() ?? ''
    report.MotorVoltageUnit = tab1Input?.actNameMotor_TVoltage || 'V'
    report.MotorCurrent = motor?.SelectedMotor?.current?.toString() ?? ''
    report.MotorDeratingPercent = motor?.MotorInput?.motorLoad?.toString() ?? '';
    report.MotorCurrentUnit = tab1Input?.actNameMotor_TCurrent || 'A'
    if (Chart1Data && Chart1Data.length > 0) {
        report.graph1Data = JSON.stringify(GenerateChart1Points(Chart1Data))
        GenerateChart1AxisPoints(Chart1Data, report)
    }
    if (Chart1Data && Chart1Data.length > 0) {
        report.graph2Data = JSON.stringify(GenerateChart3Points(Chart2Data))
        GenerateChart2AxisPoints(Chart2Data, report)
    }
    if (Chart3Data && Chart3Data.length > 0) {
        report.graph3Data = JSON.stringify(GenerateChart3Points(Chart3Data))
        GenerateChart3AxisPoints(Chart3Data, report)
    }
    if (showIPRPlotInMainReport && iprCurve) {
        report.IPRGraphData = JSON.stringify(createIPRChart(iprCurve));
        GenerateIPRChartAxisPoints(iprCurve, report)
    }
    if (showDepletionPlotInMainReport && depletionData) {
        report.DepletionGraphData = JSON.stringify(createDepletionChart(depletionData));
        GenerateDepletionChartAxisPoints(depletionData, report)
    }
    report.ShowWellSurveyGraph = showWellDataInMainReport ?? false;
    report.Notes = WellServeyNotes ?? '';
    var TailPipeBtmData = wellServey?.TailPipeBottom ? Number(wellServey?.TailPipeBottom) : 0;
    if (wellSurveyChart1?.length > 0) {
        report.WellSurveyGraphData1 = JSON.stringify(SurveyChart1(wellSurveyChart1, +(kop), TailPipeBtmData, schematicsData))
        GenerateWellSurveyChartAxisPoints(1, wellSurveyChart1, report, 1000)

        let kopPlotData: WellSurveyData[] = [];
        const kopIndex = wellSurveyChart1.findIndex((x: { md: number; }) => x.md === +(kop));
        const surveyLength = wellSurveyChart1.length - 1;
        if (kopIndex >= 0) {
            const start = kopIndex >= 18 ? kopIndex - 18 : 0;
            const end = (surveyLength - kopIndex) >= 8 ? kopIndex + 8 : surveyLength;
            kopPlotData = wellSurveyChart1.slice(start, end);
        }
        report.WellSurveyGraphData2 = JSON.stringify(SurveyChart1(kopPlotData, +(kop), TailPipeBtmData, schematicsData))
        GenerateWellSurveyChartAxisPoints(2, kopPlotData, report)
    }
    else {
        report.WellSurveyGraphData1 = '';

        report.WellSurveyGraphData2 = '';
    }
    report.DepthOfMaxDLS = depthOfMaxDLS?.toString() ?? '';
    report.MaxDLSAboveKOP = MaxDLSAboveKOP?.toString() ?? '';
    report.TailPipeBtm = wellServey?.TailPipeBottom?.toString() ?? '';
    report.TransitionZone = wellServey?.TransitionZone?.toString() ?? '';
    report.TailPipeTZ = tailPipeTZ?.toString() ?? '';
    report.ShowIPRPlotOnMainReport = showIPRPlotInMainReport;
    report.ShowDepletionPlotOnMainReport = showDepletionPlotInMainReport;
    report.TopOfEspPump = schematicsData?.eoTubing?.toString() ?? '';
    report.KOP = kop ? kop.toString() : '';
    report.EspLength = schematicsData && schematicsData?.totalEspLength ? schematicsData?.totalEspLength?.toString() : '';
    report.BottomOfEspSensor = schematicsData?.sensorDepth?.toString() ?? '';
    report.SR_TailPipeJoints = schematicsData?.miscellaneous?.tailPipeJoints?.toString() ?? '';
    const jointsStr = report?.SR_TailPipeJoints;
    const jointsNum = parseFloat(jointsStr);
    report.TailPipeDesander = (!isNaN(jointsNum) ? (jointsNum * 31.7).toFixed(1) : '');

    report.GasAnchorOD = BasicGasCalculation?.anchorOD
    report.GasAnchorODUnit = BasicGasCalculation?.actNameGas_AnchorOD
    report.GasAnchorID = BasicGasCalculation?.anchorID
    report.GasAnchorIDUnit = BasicGasCalculation?.actNameGas_AnchorID
    report.GasAnchorTubingOD = BasicGasCalculation?.tubingOD
    report.GasAnchorTubingODUnit = BasicGasCalculation?.actNameGas_AnchorTubingOD
    report.IsGasAnchorVisible = (!isInValid(report.GasAnchorOD ?? '') && !isInValid(report?.GasAnchorID ?? '') && !isInValid(report?.GasAnchorTubingOD ?? ''))
    report.GasAnchorNatSepEff = GasCalculationOutPut?.Anchor_NaturalSeperatorEff?.toString() ?? ''
    report.GasAnchorFreeGasIntake = GasCalculationOutPut?.Anchor_FreeGas?.toString() ?? ''
    report.GasAnchorFreeGasDischarge = GasCalculationOutPut?.Intake_FreeGas?.toString() ?? ''
    report.GasAnchorTFVIntake = GasCalculationOutPut?.Anchor_TotalFluidVolume?.toString() ?? ''
    report.GasAnchorTFVIntakeUnit = BasicGasCalculation?.actNameGas_TotalFluidVolume?.toString() ?? ''
    report.GasAnchorTFVDischarge = GasCalculationOutPut?.Intake_TotalFluidVolume?.toString() ?? ''
    report.GasAnchorTFVDischargeUnit = BasicGasCalculation?.actNameGas_TotalFluidVolume?.toString() ?? ''
    report.GasSep_FreeGasDischarge = GasCalculationOutPut?.SP4_FreeGas?.toString() ?? GasCalculationOutPut?.SP3_FreeGas?.toString() ?? GasCalculationOutPut?.SP2_FreeGas?.toString() ?? GasCalculationOutPut?.SP1_FreeGas?.toString() ?? ''
    report.GasSep_TVFIntake = GasCalculationOutPut?.Intake_TotalFluidVolume?.toString() ?? ''
    report.GasSep_TFVDischarge = GasCalculationOutPut?.SP4_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? ''
    report.GasSep_PressureIntake = GasCalculationOutPut?.Intake_Pressure?.toString() ?? ''
    report.GasBOIPressureDischarge = GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.GKX_FreeGasIntake = GasCalculationOutPut?.SP4_FreeGas?.toString() ?? GasCalculationOutPut?.SP3_FreeGas?.toString() ?? GasCalculationOutPut?.SP2_FreeGas?.toString() ?? GasCalculationOutPut?.SP1_FreeGas?.toString() ?? ''
    report.GKX_FreeGasDischarge = GasCalculationOutPut?.GKX_FreeGas?.toString() ?? ''
    report.GKX_TFVIntake = GasCalculationOutPut?.SP4_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? ''
    report.GKX_TFVDischarge = GasCalculationOutPut?.GKX_TotalFluidVolume?.toString() ?? ''
    report.GKX_PressureIntake = GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.GKX_PressureDischarge = GasCalculationOutPut?.GKX_Pressure?.toString() ?? ''
    report.FC_FreeGasIntake = GasCalculationOutPut?.CP2_FreeGas?.toString() ?? ''
    report.FC_FreeGasDischarge = GasCalculationOutPut?.CP3_FreeGas?.toString() ?? ''

    report.ChargPmp1_FreeGasIntake = GasCalculationOutPut?.GKX_FreeGas?.toString() ?? GasCalculationOutPut?.SP4_FreeGas?.toString() ?? GasCalculationOutPut?.SP3_FreeGas?.toString() ?? GasCalculationOutPut?.SP2_FreeGas?.toString() ?? GasCalculationOutPut?.SP1_FreeGas?.toString() ?? '';

    report.ChargPmp1_FreeGasDischarge = GasCalculationOutPut?.CP1_FreeGas?.toString() ?? ''
    report.ChargPmp2_FreeGasIntake = GasCalculationOutPut?.CP1_FreeGas?.toString() ?? ''
    report.ChargPmp2_FreeGasDischarge = GasCalculationOutPut?.CP2_FreeGas?.toString() ?? ''
    report.FC_TFVIntake = GasCalculationOutPut?.GKX_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP4_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? ''
    report.FC_TFVDischarge = GasCalculationOutPut?.CP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.CP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.CP1_TotalFluidVolume?.toString() ?? ''
    report.FC_IntakePressure = GasCalculationOutPut?.GKX_Pressure?.toString() ?? GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.FC_DischargePressure = GasCalculationOutPut?.CP3_Pressure?.toString() ?? GasCalculationOutPut?.CP2_Pressure?.toString() ?? GasCalculationOutPut?.CP1_Pressure?.toString() ?? ''
    report.ChargPmp1_TFVIntake = GasCalculationOutPut?.GKX_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? ''
    report.ChargPmp2_TFVIntake = GasCalculationOutPut?.CP1_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.GKX_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? ''
    report.ChargPmp3_TFVIntake = GasCalculationOutPut?.CP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.CP1_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.GKX_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP3_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP2_TotalFluidVolume?.toString() ?? GasCalculationOutPut?.SP1_TotalFluidVolume?.toString() ?? ''
    report.ChargPmp1_TFVDischarge = GasCalculationOutPut?.CP1_TotalFluidVolume?.toString() ?? ''
    report.ChargPmp2_TFVDischarge = GasCalculationOutPut?.CP2_TotalFluidVolume?.toString() ?? ''
    report.ChargPmp3_TFVDischarge = GasCalculationOutPut?.CP3_TotalFluidVolume?.toString() ?? ''
    report.BHPGasHandlerOnly = GasCalculationOutPut?.GKX_HPRating?.toString() ?? ''
    report.BHPChargPmp1Only = GasCalculationOutPut?.CP1_HPRating?.toString() ?? ''
    report.BHPChargPmp2Only = GasCalculationOutPut?.CP2_HPRating?.toString() ?? ''
    report.BHPChargPmp3Only = GasCalculationOutPut?.CP3_HPRating?.toString() ?? ''
    report.ChargPmp1_PressureIntake = GasCalculationOutPut?.GKX_Pressure?.toString() ?? GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.ChargPmp2_PressureIntake = GasCalculationOutPut?.CP1_Pressure?.toString() ?? GasCalculationOutPut?.GKX_Pressure?.toString() ?? GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.ChargPmp3_PressureIntake = GasCalculationOutPut?.CP2_Pressure?.toString() ?? GasCalculationOutPut?.CP1_Pressure?.toString() ?? GasCalculationOutPut?.GKX_Pressure?.toString() ?? GasCalculationOutPut?.SP4_Pressure?.toString() ?? GasCalculationOutPut?.SP3_Pressure?.toString() ?? GasCalculationOutPut?.SP2_Pressure?.toString() ?? GasCalculationOutPut?.SP1_Pressure?.toString() ?? ''
    report.ChargPmp1_PressureDischarge = GasCalculationOutPut?.CP1_Pressure?.toString() ?? ''
    report.ChargPmp2_PressureDischarge = GasCalculationOutPut?.CP2_Pressure?.toString() ?? ''
    report.ChargPmp3_PressureDischarge = GasCalculationOutPut?.CP3_Pressure?.toString() ?? ''
    report.Pump_DesiredFlowRate = tab2Input?.productionRate?.toString() ?? ''
    report.Pump_DesiredFlowRateUnit = tab2Input?.actNameProductionRate
    report.Pump_ShaftType = tab1Input?.hdn_Pump_ShaftType?.toString() === '1' ? 'High Strength' : 'Standard'
    report.Pump_HousingPressure = tab1Input?.hdn_Pump_HousingType?.toString() === '1' ? 'High Pressure' : 'Standard'

    report.Pump_HousingLoad = pumpData?.FetchPercentageHousingLoad?.toString() ?? ''
    report.Pump_PercentShaftLoad = pumpData?.FetchShaftPercentageLoad?.toString() ?? ''
    report.Pump_IntakePressure = gasFinalData?.Pressure ?? ''
    report.Pump_TFVIntake = gasFinalData?.preTFV ?? ''
    report.Pump_FreeGasIntake = gasFinalData?.FreeGasGvf ?? ''

    if (tubingCableChart && tubingCableChart[0]?.length > 0)
        report.ClearanceGraph1 = JSON.stringify(GenerateClearanceChart1Points(tubingCableChart))
    if (pumpMotorLeadChart && pumpMotorLeadChart[1]?.length > 0)
        report.ClearanceGraph2 = JSON.stringify(GenerateClearanceChart2Points(pumpMotorLeadChart))
    if (sealMotorLeadChart && sealMotorLeadChart[2]?.length > 0)
        report.ClearanceGraph3 = JSON.stringify(GenerateClearanceChart3Points(sealMotorLeadChart))
    if (motorChart && motorChart[3]?.length > 0)
        report.ClearanceGraph4 = JSON.stringify(GenerateClearanceChart4Points(motorChart));
    report.Motor_PowerRequired = motor_HPInput?.toString() ?? ''
    report.Motor_PowerRequired_Unit = tab1Input?.actNameMotor_HP
    report.Motor_OD = SelectedMotor?.motorOD?.toString() ?? ''
    report.Motor_OD_Unit = tab1Input?.actNameMotor_TMotorOD
    if (MotorInput?.motorLoad !== 100) {
        report.Motor_NamePlatePower = motor.MotorDeratingHP?.toString() ?? ''
        report.Motor_NamePlateVoltage = motor.MotorDeratingVolts?.toString() ?? ''
        report.Motor_NamePlateCurrent = motor.MotorDeratingAmps?.toString() ?? ''
    }
    else {
        report.Motor_NamePlatePower = SelectedMotor?.baseHP?.toString() ?? ''
        report.Motor_NamePlateVoltage = SelectedMotor?.voltage?.toString() ?? ''
        report.Motor_NamePlateCurrent = SelectedMotor?.current?.toString() ?? ''
    }
    report.Motor_NamePlatePower_Unit = tab1Input?.actNameMotor_TPower
    report.Motor_NamePlateVoltageUnit = tab1Input?.actNameMotor_TVoltage
    report.Motor_NamePlateCurrentUnit = tab1Input?.actNameMotor_TCurrent
    report.Motor_Velocity = fluidVelocity ?? ''
    report.Motor_VelocityUnit = tab1Input?.actNameMotor_VFluidPastMotor
    if (MotorperformanceList?.length) {
        let motorperf = MotorperformanceList[0]
        report.Motor_OperatingEfficiency = motorperf?.efficiency?.toString() ?? ''
        report.Motor_LoadAt60Hz = motorperf?.motorLoad?.toString() ?? ''
        report.Motor_OperatingCurrent = motorperf?.current?.toString() ?? ''
        report.Motor_OperatingVoltage = motorperf?.voltage?.toString() ?? ''
        report.Motor_OperatingTemperature = motorperf?.opeatngTemp?.toString() ?? ''
        report.Motor_OperatingTemperatureUnit = tab1Input?.actNameMotor_T1OperatingTemp
        report.Motor_OperatingSpeed = motorperf?.speed?.toString() ?? ''
        report.Motor_OperatingSpeedUnit = tab1Input?.actNameMotor_T1Speed
        report.Motor_OperatingPower = motorperf?.changedHP?.toString() ?? ''
        report.Motor_OperatingPowerUnit = tab1Input?.actNameMotor_TPower
        report.Motor_OperatingPowerFactor = motorperf?.powerFactor?.toString() ?? ''
        report.MotorKW = motorperf?.kwOut?.toString() ?? ''

        report.Protector_ProtectorName1 = selectedLowerProtector?.name
        report.Protector_ProtectorName2 = selectedUpperProtector?.name
        report.BearingType = tab1Input?.hdnProtector_BearingType?.toString() === '1' ? ' Ultra High Load ' : 'High Load'
        report.ShaftType = tab1Input?.hdnProtector_ShaftType?.toString() === '1' ? ' High Strength ' : 'Standard'
        if (OilContractionList) {
            report.Protector_LT_Operation_1 = OilContractionList?.operation_LTChamber1Contraction
            report.Protector_LT_Operation_2 = OilContractionList?.operation_LTChamber2Contraction
            report.Protector_LT_Operation_3 = OilContractionList?.operation_LTChamber3Contraction
            report.Protector_UT_Operation_1 = OilContractionList?.operation_UTChamber1Contraction
            report.Protector_UT_Operation_2 = OilContractionList?.operation_UTChamber2Contraction
            report.Protector_UT_Operation_3 = OilContractionList?.operation_UTChamber3Contraction
            report.Protector_LT_Pull_1 = OilContractionList?.pull_LTChamber1Contraction
            report.Protector_LT_Pull_2 = OilContractionList?.pull_LTChamber2Contraction
            report.Protector_LT_Pull_3 = OilContractionList?.pull_LTChamber3Contraction
            report.Protector_UT_Pull_1 = OilContractionList?.pull_UTChamber1Contraction
            report.Protector_UT_Pull_2 = OilContractionList?.pull_UTChamber2Contraction
            report.Protector_UT_Pull_3 = OilContractionList?.pull_UTChamber3Contraction
        }
        if (selectedCable) {
            report.Cable_CableType = (selectedCable[1] as { label?: string })?.label ?? ''
        }

        report.Cable_EstCableLength = cableTotalLength?.toString() ?? ''
        report.Cable_EstCableLength_Unit = tab1Input?.actNameCable_EstCableLength
        report.Cable_VoltageLoss = cableTotalVoltageLoss?.toString() ?? ''
        report.CablVoltLoss1000 = cableVoltageLoss1000 ? cableVoltageLoss1000.toString() : ''
        report.CableMaxConductorTemp = cableMaxCondutorTemp?.toString();
        report.CableConductorTempDesign = cableConductorTempDesign ? cableConductorTempDesign.toString() : '';
        report.CableAmpacity = cableAmpacity?.toString();
        report.CableVDrop = cableVDrop?.toString();
        report.DriftID = tab1Input?.hdnCable_IsDriftID === true ? 'Drift' : 'Nominal'
        report.CasingNominalID = report?.DriftID === 'Drift' ? tab1Input?.casingDriftID : tab1Input?.casingID
        if (report.DriftID === 'Drift') {
            report.NominalID = 'Drift ID :'
        }
        else {
            report.NominalID = 'Nominal ID :'
        }

        if (cableClearance) {
            report.Cable_Clearance1 = cableClearance?.ClrnceForTubng
            report.Cable_Clearance2 = cableClearance?.ClrnceForPmp
            report.Cable_Clearance3 = cableClearance?.ClrnceForPrtctr
            report.Cable_Clearance4 = cableClearance?.ClrnceForMotr
            report.Cable_Clearance_Unit = tab1Input?.actNameCable_ClearanceTubing
        }

        report.Surface_ControllerType = tab1Input?.hdnCntrlTyp === '1' ? 'Switchboard' : 'VSD'
        report.Surface_Controller = tab1Input?.hdnCntrlTyp === '1' ? (selectedSwitchBoard?.description ?? '') : SelectedVSD?.descriptionField;
        report.Surface_PercentageControllerLoad = tab1Input?.hdnCntrlTyp === '1' ? surfaceEquipment?.FetchSwitchControllerLoad?.toString() ?? '' : surfaceEquipment?.FetchVSDControllerLoad?.toString() ?? ''
        report.Surface_PowerFactor = tab1Input?.hdnSurface_PwrFactr?.toString() ?? '0.77'
        report.Surface_ElectricalCost = basicElectrictyCost ?? ''
        report.Surface_ElectricalCost_Unit = tab1Input?.actNameSurface_BasicCost
        report.MotorDerating_HP = motor?.MotorDeratingHP?.toString() ?? '';
        report.MotorDerating_Voltage = motor?.MotorDeratingVolts?.toString() ?? '';
        report.MotorDerating_Amps = motor?.MotorDeratingAmps?.toString() ?? '';
        report.HarmonicFilterSelection = harmonicFilter?.FilterSelection?.name?.toString() ?? "";
        report.IsHarmonicFilterVisible = !isInValid(report?.HarmonicFilterSelection);
        report.GKXShaftLoading = GKXCalculationData?.shaftLoad?.toString() ?? '';
        report.GKXEfficiency = GKXCalculationData?.efficiency?.toString() ?? '';
        report.GKXTotalPower = GKXCalculationData?.totalPower?.toString() ?? '';
        report.GKXFinalTDH = GKXCalculationData?.finalTDH?.toString() ?? '';
        report.GKXLiftStage = GKXCalculationData?.liftPerStage?.toString() ?? '';
        report.GKXShaftType = BasicGasCalculation?.hdn_GKX_ShaftType?.toString() === '1' ? 'Standard' : 'High Strength'
        report.GKXNoOfStage = GKXCalculationData?.desiredStages?.toString() ?? '';
        report.ChgPum3Efficiency = CP3CalculationData?.efficiency?.toString() ?? '';
        report.ChgPum2Efficiency = CP2CalculationData?.efficiency?.toString() ?? '';
        report.ChgPum1Efficiency = CP1CalculationData?.efficiency?.toString() ?? '';
        report.ChgPum3TotalPower = CP3CalculationData?.totalPower?.toString() ?? '';
        report.ChgPum2TotalPower = CP2CalculationData?.totalPower?.toString() ?? '';
        report.ChgPum1TotalPower = CP1CalculationData?.totalPower?.toString() ?? '';
        report.ChgPum3FinalTDH = CP3CalculationData?.finalTDH?.toString() ?? '';
        report.ChgPum2FinalTDH = CP2CalculationData?.finalTDH?.toString() ?? '';
        report.ChgPum1FinalTDH = CP1CalculationData?.finalTDH?.toString() ?? '';
        report.ChgPum3LiftStage = CP3CalculationData?.liftPerStage?.toString() ?? '';
        report.ChgPum2LiftStage = CP2CalculationData?.liftPerStage?.toString() ?? '';
        report.ChgPum1LiftStage = CP1CalculationData?.liftPerStage?.toString() ?? '';
        report.ChgPum3ShaftType = BasicGasCalculation?.hdn_ChargePump3_ShaftType?.toString() === '1' ? 'Standard' : 'High Strength'
        report.ChgPum2ShaftType = BasicGasCalculation?.hdn_ChargePump2_ShaftType?.toString() === '1' ? 'Standard' : 'High Strength'
        report.ChgPum1ShaftType = BasicGasCalculation?.hdn_ChargePump1_ShaftType?.toString() === '1' ? 'Standard' : 'High Strength'
        report.ChargPm1ShaftLoading = CP1CalculationData?.shaftLoad?.toString() ?? '';
        report.ChargPmp2ShaftLoading = CP2CalculationData?.shaftLoad?.toString() ?? '';
        report.ChargPmp3ShaftLoading = CP3CalculationData?.shaftLoad?.toString() ?? '';
        report.Region = selectedRegion?.name?.toString() ?? '';
        var IntkPres = ''
        if (wellInflowData && wellInflowData?.[0] != null) {
            IntkPres = tab2Input?.intakePressure?.toString() ?? ''
        }
        let compSPGr = tab3Input?.combinedSpGr?.toString() || '0';
        let GOR = tab3Input?.gor?.toString() || '0';

        let spGrGas = tab3Input?.spGrGas?.toString() || '0';
        let FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
        let GasComprs = tab3Input?.gasCompressibility?.toString() || '0';
        let gasAvail = tab3Input?.gasAvailblty?.toString() || '0';
        let bubbPres = tab3Input?.bubblePoint?.toString() || '0';

        let desrdNoOfStge = pumpInputData?.pump_DesrdStges?.toString();
        if (desrdNoOfStge == '' || desrdNoOfStge == null || desrdNoOfStge == undefined) {
            desrdNoOfStge = '0';
        }
        if (gasAvail === "0") {
            GOR = "0";
            GasComprs = "0";
            bubbPres = "0";
            spGrGas = "0";
        }
        GOR = gasFinalData?.GOR ?? GOR
        compSPGr = gasFinalData?.compSPGr ?? compSPGr
        if (tab2Input?.inflowTypeLstVal === '2') {
            FLOP = tab2Input?.flop == null || undefined ? 0 : tab2Input?.flop;
        }
        else {
            FLOP = gasFinalData?.FLOP != null ? +gasFinalData.FLOP : FLOP;
        }
        report.ProductivityIndex = Number(inflowOutput?.productivityIndex).toFixed(2);
        report.StaticBHP = Number(inflowOutput?.staticBottomHolePressure).toFixed(2);
        report.FlowingBHP = Number(inflowOutput?.flowingBottomHolePressure).toFixed(2);
        report.StaticFL = Number(inflowOutput?.staticFluidLevel).toFixed(2);
        report.ProductionFL = Number(inflowOutput?.producingFluidLevel).toFixed(2);
        report.PI_Unit = tab2Input?.actNamePI ?? '';
        report.StaticBHP_Unit = tab2Input?.actNameSBHP ?? '';
        report.FlowingBHP_Unit = tab2Input?.actNameFBHP ?? '';
        report.StaticFL_Unit = tab2Input?.actNameStaticFluidLevel ?? '';
        report.ProductionFL_Unit = tab2Input?.actNameProducingFluidLevel ?? '';

        report.TotalTDH = parseFloat(totalSystemTDH);
        report.RequiredSystemTDH = Number(basicTDHCalculation?.systemTDH);
        if (pumpIterationLast) {
            report.Pump_FreeGasDischarge = pumpIterationLast?.chargePumpSepFreeGas || '';
            report.Pump_PressureDischarge = pumpIterationLast?.chargePumpTotalPressure || '';
            report.Pump_TFVDischarge = pumpIterationLast?.chargePumpSepTFV || '';
        }

        report.SR_SurfVolts = maxvoltage?.[0]?.max?.toString() ?? '0';
        //Schematic Data
        if (schematicsData) {
            GetSchematicData(schematicsData, report);
        }

        //Summary
        //Below ESP
        report.SR_MLELengthSplice = schematicsData?.mleLength?.toString() ?? '';
        let selectedMotorSensor = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRSensor) || [];
        report.Motor_Sensor = selectedMotorSensor?.[0]?.equipmentName ?? '';
        let belowESPData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRAccBelowESP);
        var des = (belowESPData && belowESPData?.length > 0) ? EquipmentsBelowESP.ESPEquipmentsForBelow?.filter(x => x.id === belowESPData?.[0]?.equipmentId) : [];

        report.SR_DeSanderName = (belowESPData ?? [])[0]?.equipmentName ?? '';// verify
        report.SR_DeSanderName = des?.[0]?.equipmentName;
        report.SR_DeSanderPN = ((belowESPData ?? []).length > 0 ? belowESPData?.[0]?.pn ?? '' : '').toString();
        report.SR_DeSanderLength = (belowESPData?.[0]?.length ?? '').toString();
        report.SR_DeSanderWeight = (belowESPData?.[0]?.weight ?? '').toString();
        report.SR_DeSanderSetDepth = (belowESPData?.[0]?.setDepth ?? '').toString();

        //Transformer
        report.SR_Transformer = SelectedTransformer?.equipmentName ?? SelectedTransformer?.transformers ?? '';
        report.SR_HarmonicFilterPN = harmonicFilter.FilterSelection?.harmPN?.toString() ?? '';
        report.SR_PumpSeries = selectedPumpData?.pumpSeries ?? '';
        var totalVolt: number = 0;
        var totalAmp: number = 0;
        var totalHP: number = 0;
        ESPEquipments.forEach(motor => {
            var d = Equipments?.filter(x => x?.id === motor?.id);
            totalVolt = totalVolt + (d[0]?.voltage ?? 0);
            totalAmp = totalAmp + (d[0]?.current ?? 0);
            totalHP = totalHP + (d[0]?.size ?? 0);
        })
        var seriesType: string = SelectedMotor?.seriesType ?? '';
        var motorHeader: string = totalHP + '/' + totalVolt + '/' + totalAmp + ' ' + seriesType;
        report.SR_MotorHeader = motorHeader;
        report.SR_SurfaceCableLengthQuantity = tab1Input?.hdnCable_SurfCabl?.toString() ?? ''
        report.ShowSchematicData = schematicsData?.miscellaneous?.showSchematicData;
    }

    let mainReportPayload = {
        inputs: report,
        projectId: tab1Input?.projectIDNew.toString(),
        unitSystem: "0",
    };

    return mainReportPayload;
};

const GenerateWellSurveyChartAxisPoints = (chartId: number, chartData: WellSurveyData[], report: MainReport, mdOffset: number = 0) => {
    const dlsMax = Math.max(...chartData.map(o => o.dls)) + 2;
    const mdMax = Math.max(...chartData.map(o => o.md)) + mdOffset;
    const incMax = Math.max(...chartData.map(o => o.inc)) + 10;

    if (chartId == 2) {
        report.INCMax2 = incMax?.toString();
        report.DLSMax2 = dlsMax?.toString();
        report.MDMax2 = mdMax?.toString();
        report.MinMd2 = (chartData?.length ? chartData?.[0]?.md : 0).toString()
    } else {
        report.INCMax1 = incMax?.toString();
        report.DLSMax1 = dlsMax?.toString();
        report.MDMax1 = mdMax?.toString();
        report.MinMd1 = (chartData?.length ? chartData?.[0]?.md : 0).toString()
    }
}

const SurveyChart1 = (chartData: WellSurveyData[], kop: number, tailPipeBottom: number, schematicsData: any) => {
    let chartDataArray: any[] = []

    chartData.forEach((data, i) => {
        var point = new chartDataModel()
        point.SeriesName = "inc"
        point.PointValue = {
            IsEmpty: false,
            X: data.inc,
            Y: data.md,

        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)

    });

    const kopIndex = chartData.findIndex(x => x.md === kop);
    chartData.forEach((data, i) => {
        var point = new chartDataModel()
        point.SeriesName = "DLS"
        point.PointValue = {
            IsEmpty: false,
            X: data.dls,
            Y: data.md
        }
        if (i === kopIndex || i === kopIndex - 1) {
            point.Note = `DLS, ${data?.dls}, ${data?.md}`
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)
    });
    var eoTubing = schematicsData?.eoTubing ?? 0;
    var sensorDepth = schematicsData ? Number(schematicsData?.sensorDepth) : 0;
    if (schematicsData && eoTubing > 0) {
        [{ dls: 2, md: 0 }, { dls: 2, md: schematicsData?.eoTubing }].forEach((data, i) => {
            var point = new chartDataModel()
            point.SeriesName = "Tubing"
            point.PointValue = {
                IsEmpty: false,
                X: data.dls,
                Y: data.md
            }
            point.PointNo = "Point" + i.toString()
            chartDataArray.push(point)

        });
    }
    if (eoTubing > 0 && sensorDepth > 0) {
        [{ dls: 2, md: eoTubing }, { dls: 2, md: sensorDepth }].forEach((data, i) => {
            var point = new chartDataModel()
            point.SeriesName = "ESP"
            point.PointValue = {
                IsEmpty: false,
                X: data.dls,
                Y: data.md
            }
            point.PointNo = "Point" + i.toString()
            chartDataArray.push(point)

        });
    }
    if (sensorDepth > 0 && tailPipeBottom > 0) {
        [{ dls: 2, md: sensorDepth }, { dls: 2, md: tailPipeBottom }].forEach((data, i) => {
            var point = new chartDataModel()
            point.SeriesName = "TailPipe"
            point.PointValue = {
                IsEmpty: false,
                X: data.dls,
                Y: data.md
            }
            point.PointNo = "Point" + i.toString()
            chartDataArray.push(point)

        });
    }

    return chartDataArray
};

const GetSchematicData = (schematicsData: SchematicsModel, report: MainReport) => {
    report.SR_EOTubing = schematicsData?.eoTubing ? parseFloat(schematicsData?.eoTubing?.toString()).toFixed(2) : '';
    report.SR_IntakeSetDepth = schematicsData?.intakeSetDepth?.toString() ?? '';
    report.SR_ESPBottomSetDepth = schematicsData?.sensorDepth?.toString() ?? '';//verify
    report.SR_SetDepth = schematicsData?.setDepth?.toString() ?? '';
    report.SR_ESPLength = schematicsData?.totalEspLength?.toString() ?? '';
    report.SR_ESPTailPipeDesanderLength = schematicsData?.espTailPipeDescender?.toString() ?? '';
    report.SR_EOTubing = schematicsData?.eoTubing?.toString() ?? '';
    report.SR_DownholeESPWeight = schematicsData?.downholeEquipmentWeight?.toString() ?? '';

    // BOH
    let selectedBOH = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRBOH);
    report.SR_BOHName = (selectedBOH && selectedBOH.length > 0) ? selectedBOH[0]?.equipmentName : '';
    report.SR_BOHPN = (selectedBOH && selectedBOH.length > 0) ? selectedBOH[0]?.pn?.toString() ?? '' : ""
    report.SR_BOHLength = (selectedBOH && selectedBOH.length > 0) ? selectedBOH[0]?.length?.toString() ?? '' : ""
    report.SR_BOHWeight = (selectedBOH && selectedBOH.length > 0) ? selectedBOH[0]?.weight?.toString() ?? '' : ""
    report.SR_BOHSetDepth = (selectedBOH && selectedBOH.length > 0) ? selectedBOH[0]?.setDepth?.toString() ?? '' : ""

    //pump data
    let selectedPumpList = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRPumps);
    report.SR_Pump1Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[0]?.equipmentName ?? '' : " ";
    report.SR_Pump1PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[0]?.pn?.toString() ?? '' : "";
    report.SR_Pump1Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[0]?.length?.toString() ?? '' : "";
    report.SR_Pump1Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[0]?.weight?.toString() ?? '' : "";
    report.SR_Pump1SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[0]?.setDepth?.toString() ?? '' : "";

    report.SR_Pump2Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[1]?.equipmentName ?? '' : "";
    report.SR_Pump2PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[1]?.pn?.toString() ?? '' : "";
    report.SR_Pump2Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[1]?.length?.toString() ?? '' : "";
    report.SR_Pump2Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[1]?.weight?.toString() ?? '' : "";
    report.SR_Pump2SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[1]?.setDepth?.toString() ?? '' : "";

    report.SR_Pump3Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[2]?.equipmentName ?? '' : "";
    report.SR_Pump3PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[2]?.pn?.toString() ?? '' : "";
    report.SR_Pump3Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[2]?.length?.toString() ?? '' : "";
    report.SR_Pump3Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[2]?.weight?.toString() ?? '' : "";
    report.SR_Pump3SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[2]?.setDepth?.toString() ?? '' : "";

    report.SR_Pump4Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[3]?.equipmentName ?? '' : "";
    report.SR_Pump4PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[3]?.pn?.toString() ?? '' : "";
    report.SR_Pump4Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[3]?.length?.toString() ?? '' : "";
    report.SR_Pump4Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[3]?.weight?.toString() ?? '' : "";
    report.SR_Pump4SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[3]?.setDepth?.toString() ?? '' : "";

    let Pump5Data = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRChargePump3) || [];
    if (Pump5Data && Pump5Data.length > 0) {
        report.SR_Pump5Name = Pump5Data?.[0]?.equipmentName ?? '';
        report.SR_Pump5PN = Pump5Data?.[0]?.pn?.toString() ?? '';
        report.SR_Pump5Length = Pump5Data?.[0]?.length?.toString() ?? '';
        report.SR_Pump5Weight = Pump5Data?.[0]?.weight?.toString() ?? '';
        report.SR_Pump5SetDepth = Pump5Data?.[0]?.setDepth?.toString() ?? '';
    } else {
        report.SR_Pump5Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[4]?.equipmentName ?? '' : "";
        report.SR_Pump5PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[4]?.pn?.toString() ?? '' : "";
        report.SR_Pump5Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[4]?.length?.toString() ?? '' : "";
        report.SR_Pump5Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[4]?.weight?.toString() ?? '' : "";
        report.SR_Pump5SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[4]?.setDepth?.toString() ?? '' : "";
    }

    let Pump6Data = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRChargePump2) || [];
    if (Pump6Data && Pump6Data.length > 0) {
        report.SR_Pump6Name = Pump6Data?.[0]?.equipmentName ?? '';
        report.SR_Pump6PN = Pump6Data?.[0]?.pn?.toString() ?? '';
        report.SR_Pump6Length = Pump6Data?.[0]?.length?.toString() ?? '';
        report.SR_Pump6Weight = Pump6Data?.[0]?.weight?.toString() ?? '';
        report.SR_Pump6SetDepth = Pump6Data?.[0]?.setDepth?.toString() ?? '';
    } else {
        report.SR_Pump6Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[5]?.equipmentName ?? '' : "";
        report.SR_Pump6PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[5]?.pn?.toString() ?? '' : "";
        report.SR_Pump6Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[5]?.length?.toString() ?? '' : "";
        report.SR_Pump6Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[5]?.weight?.toString() ?? '' : "";
        report.SR_Pump6SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[5]?.setDepth?.toString() ?? '' : "";
    }

    let Pump7Data = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRChargePump1) || [];
    if (Pump7Data && Pump7Data.length > 0) {
        report.SR_Pump7Name = Pump7Data?.[0]?.equipmentName ?? '';
        report.SR_Pump7PN = Pump7Data?.[0]?.pn?.toString() ?? '';
        report.SR_Pump7Length = Pump7Data?.[0]?.length?.toString() ?? '';
        report.SR_Pump7Weight = Pump7Data?.[0]?.weight?.toString() ?? '';
        report.SR_Pump7SetDepth = Pump7Data?.[0]?.setDepth?.toString() ?? '';
    }
    else {
        report.SR_Pump7Name = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[6]?.equipmentName ?? '' : "";
        report.SR_Pump7PN = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[6]?.pn?.toString() ?? '' : "";
        report.SR_Pump7Length = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[6]?.length?.toString() ?? '' : "";
        report.SR_Pump7Weight = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[6]?.weight?.toString() ?? '' : "";
        report.SR_Pump7SetDepth = (selectedPumpList && selectedPumpList.length > 0) ? selectedPumpList?.[6]?.setDepth?.toString() ?? '' : "";
    }

    // cable MLE
    let selectedCableData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRCables) || [];
    report.SR_CableName = selectedCableData?.[0]?.equipmentName ?? '';
    report.SR_CablePN = selectedCableData?.[0]?.pn?.toString() ?? '';
    report.SR_CableLength = selectedCableData?.[0]?.length?.toString() ?? '';
    report.SR_CableWeight = selectedCableData?.[0]?.weight?.toString() ?? '';
    report.SR_CableDimension = selectedCableData?.[0]?.diameter?.toString() ?? '';

    let selectedMLEData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRMLE) || [];
    report.SR_MLEName = selectedMLEData?.[0]?.equipmentName ?? '';
    report.SR_MLEPN = selectedMLEData?.[0]?.pn?.toString() ?? '';
    report.SR_MLELength = selectedMLEData?.[0]?.length?.toString() ?? '';
    report.SR_MLEWeight = selectedMLEData?.[0]?.weight?.toString() ?? '';
    report.SR_MLEDimension = selectedMLEData?.[0]?.diameter?.toString() ?? '';

    // Gas handler
    let selectedGasHandlerData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRGasHandlers) || [];
    report.SR_GasHandler1Name = selectedGasHandlerData?.[0]?.equipmentName ?? '';
    report.SR_GasHandler1PN = selectedGasHandlerData?.[0]?.pn?.toString() ?? '';
    report.SR_GasHandler1Length = selectedGasHandlerData?.[0]?.length?.toString() ?? '';
    report.SR_GasHandler1Weight = selectedGasHandlerData?.[0]?.weight?.toString() ?? '';
    report.SR_GasHandler1SetDepth = selectedGasHandlerData?.[0]?.setDepth?.toString() ?? '';

    report.SR_GasHandler2Name = selectedGasHandlerData?.[1]?.equipmentName ?? '';
    report.SR_GasHandler2PN = selectedGasHandlerData?.[1]?.pn?.toString() ?? '';
    report.SR_GasHandler2Length = selectedGasHandlerData?.[1]?.length?.toString() ?? '';
    report.SR_GasHandler2Weight = selectedGasHandlerData?.[1]?.weight?.toString() ?? '';
    report.SR_GasHandler2SetDepth = selectedGasHandlerData?.[1]?.setDepth?.toString() ?? '';

    //accessories above esp
    let accessoriesESPData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRAccAboveESP) || [];
    report.SR_AccessoriesAboveESPName = accessoriesESPData?.[0]?.equipmentName ?? '';
    report.SR_AccessoriesAboveESPPN = accessoriesESPData?.[0]?.pn?.toString() ?? '';
    report.SR_AccessoriesAboveESPLength = accessoriesESPData?.[0]?.length?.toString() ?? '';
    report.SR_AccessoriesAboveESPWeight = accessoriesESPData?.[0]?.weight?.toString() ?? '';
    report.SR_AccessoriesAboveESPSetDepth = accessoriesESPData?.[0]?.setDepth?.toString() ?? '';


    report.SR_AccessoriesAboveESPName2 = accessoriesESPData?.[1]?.equipmentName ?? '';
    report.SR_AccessoriesAboveESPPN2 = accessoriesESPData?.[1]?.pn?.toString() ?? '';
    report.SR_AccessoriesAboveESPLength2 = accessoriesESPData?.[1]?.length?.toString() ?? '';
    report.SR_AccessoriesAboveESPWeight2 = accessoriesESPData?.[1]?.weight?.toString() ?? '';
    report.SR_AccessoriesAboveESPSetDepth2 = accessoriesESPData?.[1]?.setDepth?.toString() ?? '';

    report.SR_AccessoriesAboveESPName3 = accessoriesESPData?.[2]?.equipmentName ?? '';
    report.SR_AccessoriesAboveESPPN3 = accessoriesESPData?.[2]?.pn?.toString() ?? '';
    report.SR_AccessoriesAboveESPLength3 = accessoriesESPData?.[2]?.length?.toString() ?? '';
    report.SR_AccessoriesAboveESPWeight3 = accessoriesESPData?.[2]?.weight?.toString() ?? '';
    report.SR_AccessoriesAboveESPSetDepth3 = accessoriesESPData?.[2]?.setDepth?.toString() ?? '';

    report.SR_AccessoriesAboveESPName4 = accessoriesESPData?.[3]?.equipmentName ?? '';
    report.SR_AccessoriesAboveESPPN4 = accessoriesESPData?.[3]?.pn?.toString() ?? '';
    report.SR_AccessoriesAboveESPLength4 = accessoriesESPData?.[3]?.length?.toString() ?? '';
    report.SR_AccessoriesAboveESPWeight4 = accessoriesESPData?.[3]?.weight?.toString() ?? '';
    report.SR_AccessoriesAboveESPSetDepth4 = accessoriesESPData?.[3]?.setDepth?.toString() ?? '';


    //Gas Separators
    let selectedGasSeparator = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRGasSeparators) || [];
    report.SR_GasSeperator1Name = selectedGasSeparator?.[0]?.equipmentName ?? '';
    report.SR_GasSeperator1PN = selectedGasSeparator[0]?.pn?.toString() ?? '';
    report.SR_GasSeperator1Length = selectedGasSeparator?.[0]?.length?.toString() ?? '';
    report.SR_GasSeperator1Weight = selectedGasSeparator?.[0]?.weight?.toString() ?? '';
    report.SR_GasSeperator1SetDepth = selectedGasSeparator?.[0]?.setDepth?.toString() ?? '';

    report.SR_GasSeperator2Name = selectedGasSeparator?.[1]?.equipmentName ?? '';
    report.SR_GasSeperator2PN = selectedGasSeparator?.[1]?.pn?.toString() ?? '';
    report.SR_GasSeperator2Length = selectedGasSeparator?.[1]?.length?.toString() ?? '';
    report.SR_GasSeperator2Weight = selectedGasSeparator?.[1]?.weight?.toString() ?? '';
    report.SR_GasSeperator2SetDepth = selectedGasSeparator?.[1]?.setDepth?.toString() ?? '';

    report.SR_GasSeperator3Name = selectedGasSeparator?.[2]?.equipmentName ?? '';
    report.SR_GasSeperator3PN = selectedGasSeparator?.[2]?.pn?.toString() ?? '';
    report.SR_GasSeperator3Length = selectedGasSeparator?.[2]?.length?.toString() ?? '';
    report.SR_GasSeperator3Weight = selectedGasSeparator?.[2]?.weight?.toString() ?? '';
    report.SR_GasSeperator3SetDepth = selectedGasSeparator?.[2]?.setDepth?.toString() ?? '';

    report.SR_GasSeperator4Name = selectedGasSeparator?.[3]?.equipmentName ?? '';
    report.SR_GasSeperator4PN = selectedGasSeparator?.[3]?.pn?.toString() ?? '';
    report.SR_GasSeperator4Length = selectedGasSeparator?.[3]?.length?.toString() ?? '';
    report.SR_GasSeperator4Weight = selectedGasSeparator?.[3]?.weight?.toString() ?? '';
    report.SR_GasSeperator4SetDepth = selectedGasSeparator?.[3]?.setDepth?.toString() ?? '';

    // Protectors
    let selectedProtector = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRProtectors) || [];
    report.SR_Protector1Name = selectedProtector?.[0]?.equipmentName ?? '';
    report.SR_Protector1PN = selectedProtector?.[0]?.pn?.toString() ?? '';
    report.SR_Protector1Length = selectedProtector?.[0]?.length?.toString() ?? '';
    report.SR_Protector1Weight = selectedProtector?.[0]?.weight?.toString() ?? '';
    report.SR_Protector1SetDepth = selectedProtector?.[0]?.setDepth?.toString() ?? '';

    report.SR_Protector2Name = selectedProtector?.[1]?.equipmentName ?? '';
    report.SR_Protector2PN = selectedProtector?.[1]?.pn?.toString() ?? '';
    report.SR_Protector2Length = selectedProtector?.[1]?.length?.toString() ?? '';
    report.SR_Protector2Weight = selectedProtector?.[1]?.weight?.toString() ?? '';
    report.SR_Protector2SetDepth = selectedProtector?.[1]?.setDepth?.toString() ?? '';

    //Motors

    let selectedMotorData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRMotors) || [];
    report.SR_Motor1Name = selectedMotorData?.[0]?.equipmentName ?? '';
    report.SR_Motor1PN = selectedMotorData?.[0]?.pn?.toString() ?? '';
    report.SR_Motor1Length = selectedMotorData?.[0]?.length?.toString() ?? '';
    report.SR_Motor1Weight = selectedMotorData?.[0]?.weight?.toString() ?? '';
    report.SR_Motor1SetDepth = selectedMotorData?.[0]?.setDepth?.toString() ?? '';

    report.SR_Motor2Name = selectedMotorData?.[1]?.equipmentName ?? '';
    report.SR_Motor2PN = selectedMotorData?.[1]?.pn?.toString() ?? '';
    report.SR_Motor2Length = selectedMotorData?.[1]?.length?.toString() ?? '';
    report.SR_Motor2Weight = selectedMotorData?.[1]?.weight?.toString() ?? '';
    report.SR_Motor2SetDepth = selectedMotorData?.[1]?.setDepth?.toString() ?? '';

    report.SR_Motor3Name = selectedMotorData?.[2]?.equipmentName ?? '';
    report.SR_Motor3PN = selectedMotorData?.[2]?.pn?.toString() ?? '';
    report.SR_Motor3Length = selectedMotorData?.[2]?.length?.toString() ?? '';
    report.SR_Motor3Weight = selectedMotorData?.[2]?.weight?.toString() ?? '';
    report.SR_Motor3SetDepth = selectedMotorData?.[2]?.setDepth?.toString() ?? '';

    report.SR_Motor4Name = selectedMotorData?.[3]?.equipmentName ?? '';
    report.SR_Motor4PN = selectedMotorData?.[3]?.pn?.toString() ?? '';
    report.SR_Motor4Length = selectedMotorData?.[3]?.length?.toString() ?? '';
    report.SR_Motor4Weight = selectedMotorData?.[3]?.weight?.toString() ?? '';
    report.SR_Motor4SetDepth = selectedMotorData?.[3]?.setDepth?.toString() ?? '';

    //sensor
    let selectedMotorSensor = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRSensor) || [];
    report.SR_SensorName = selectedMotorSensor?.[0]?.equipmentName ?? '';
    report.SR_SensorPN = selectedMotorSensor?.[0]?.pn?.toString() ?? '';
    report.SR_SensorLength = selectedMotorSensor?.[0]?.length?.toString() ?? '';
    report.SR_SensorWeight = selectedMotorSensor?.[0]?.weight?.toString() ?? '';
    report.SR_SensorSetDepth = selectedMotorSensor?.[0]?.setDepth?.toString() ?? '';



    //VSD
    let VSDData = schematicsData?.equipmentsList?.filter(equipment => equipment?.equipmentType === EquipmentType.SRVSDs) || [];
    report.SR_VSDDrive = VSDData?.[0]?.equipmentName ?? '';
    report.SR_VSDDrivePN = VSDData?.[0]?.pn?.toString() ?? '';
    report.SR_StartUpFreq = schematicsData?.miscellaneous?.startupFrequency?.toString() ?? ''
    report.SR_MinFreqatDeparture = schematicsData?.miscellaneous?.minFrequency?.toString() ?? '';
    report.SR_MaxFrequency = schematicsData?.miscellaneous?.maxFrequency?.toString() ?? ''
    report.SR_TailPipeWeight = schematicsData?.miscellaneous?.tailPipeWeight?.toString() ?? ''
    report.SR_Notes = schematicsData?.miscellaneous?.note ?? '';
    report.SR_TailPipeJoints = schematicsData?.miscellaneous?.tailPipeJoints?.toString() ?? '';

    const jointsStr = report?.SR_TailPipeJoints;
    const jointsNum = parseFloat(jointsStr);

    report.SR_TailPipeLength = (!isNaN(jointsNum) ? (jointsNum * 31.7).toFixed(1) : '');
    report.SR_TailPipeSetDepth = schematicsData?.setDepth?.toString() ?? schematicsData?.miscellaneous?.tailPipeSetDepth?.toString() ?? '';
    report.SR_SurfaceCableLengthRequired = (schematicsData?.miscellaneous?.miscList?.length ? schematicsData?.miscellaneous?.miscList?.[0]?.isRequired : undefined) === true ? 'Yes' : ((schematicsData?.miscellaneous?.miscList?.length ? schematicsData?.miscellaneous?.miscList?.[0]?.isRequired : undefined) === false ? 'No' : '');
    report.SR_SurfaceCableLengthProvider = schematicsData?.miscellaneous?.miscList?.[0]?.provider?.toString() ?? '';
    report.SR_SurfaceCableLengthType = schematicsData?.miscellaneous?.miscList?.[0]?.type?.toString() ?? '';

    report.SR_CableClampsRequired = schematicsData?.miscellaneous?.miscList?.[1]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[1]?.isRequired === false ? 'No' : '');
    report.SR_CableClampsProvider = schematicsData?.miscellaneous?.miscList?.[1]?.provider?.toString() ?? '';
    report.SR_CableClampsType = schematicsData?.miscellaneous?.miscList?.[1]?.type?.toString() ?? '';
    report.SR_CableClampsQuantity = (schematicsData?.miscellaneous?.miscList?.[1]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[1]?.quantity?.toString();


    report.SR_MLEClampsRequired = schematicsData?.miscellaneous?.miscList?.[2]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[2]?.isRequired === false ? 'No' : '');
    report.SR_MLEClampsProvider = schematicsData?.miscellaneous?.miscList?.[2]?.provider?.toString() ?? '';
    report.SR_MLEClampsType = schematicsData?.miscellaneous?.miscList?.[2]?.type?.toString() ?? '';
    report.SR_MLEClampsQuantity = (schematicsData?.miscellaneous?.miscList?.[2]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[2]?.quantity?.toString();

    report.SR_MLEGuardRequired = schematicsData?.miscellaneous?.miscList?.[3]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[3]?.isRequired === false ? 'No' : '');
    report.SR_MLEGuardProvider = schematicsData?.miscellaneous?.miscList?.[3]?.provider.toString() ?? '';
    report.SR_MLEGuardType = schematicsData?.miscellaneous?.miscList?.[3]?.type.toString() ?? '';
    report.SR_MLEGuardQuantity = (schematicsData?.miscellaneous?.miscList?.[3]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[3]?.quantity?.toString();

    report.SR_SandGuardRequired = schematicsData?.miscellaneous?.miscList?.[4]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[4]?.isRequired === false ? 'No' : '');
    report.SR_SandGuardProvider = schematicsData?.miscellaneous?.miscList?.[4]?.provider?.toString() ?? '';
    report.SR_SandGuardType = schematicsData?.miscellaneous?.miscList?.[4]?.type?.toString() ?? '';
    report.SR_SandGuardQuantity = (schematicsData?.miscellaneous?.miscList?.[4]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[4]?.quantity?.toString();

    report.SR_DesanderRequired = schematicsData?.miscellaneous?.miscList?.[5]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[5]?.isRequired === false ? 'No' : '');
    report.SR_DesanderProvider = schematicsData?.miscellaneous?.miscList?.[5]?.provider?.toString() ?? '';
    report.SR_DesanderType = schematicsData?.miscellaneous?.miscList?.[5]?.type?.toString() ?? '';
    report.SR_DesanderQuantity = (schematicsData?.miscellaneous?.miscList?.[5]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[5]?.quantity?.toString();

    report.SR_GasGuardRequired = schematicsData?.miscellaneous?.miscList?.[6]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[6]?.isRequired === false ? 'No' : '');
    report.SR_GasGuardProvider = schematicsData?.miscellaneous?.miscList?.[6]?.provider?.toString() ?? '';
    report.SR_GasGuardType = schematicsData?.miscellaneous?.miscList?.[6]?.type?.toString() ?? '';
    report.SR_GasGuardQuantity = (schematicsData?.miscellaneous?.miscList?.[6]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[6]?.quantity?.toString();

    report.SR_DrainValveRequired = schematicsData?.miscellaneous?.miscList?.[7]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[7]?.isRequired === false ? 'No' : '');
    report.SR_DrainValveProvider = schematicsData?.miscellaneous?.miscList?.[7]?.provider?.toString() ?? '';
    report.SR_DrainValveType = schematicsData?.miscellaneous?.miscList?.[7]?.type?.toString() ?? '';
    report.SR_DrainValveQuantity = (schematicsData?.miscellaneous?.miscList?.[7]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[7]?.quantity?.toString();

    report.SR_CheckValveRequired = schematicsData?.miscellaneous?.miscList?.[8]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[8]?.isRequired === false ? 'No' : '');
    report.SR_CheckValveProvider = schematicsData?.miscellaneous?.miscList?.[8]?.provider?.toString() ?? '';
    report.SR_CheckValveType = schematicsData?.miscellaneous?.miscList?.[8]?.type?.toString() ?? '';
    report.SR_CheckValveQuantity = (schematicsData?.miscellaneous?.miscList?.[8]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[8]?.quantity?.toString();

    report.SR_CentralizerRequired = schematicsData?.miscellaneous?.miscList?.[9]?.isRequired
        ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[9]?.isRequired === false ? 'No' : '');
    report.SR_CentralizerProvider = schematicsData?.miscellaneous?.miscList?.[9]?.provider?.toString() ?? '';
    report.SR_CentralizerType = schematicsData?.miscellaneous?.miscList?.[9]?.type?.toString() ?? '';
    report.SR_CentralizerQuantity = (schematicsData?.miscellaneous?.miscList?.[9]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[9]?.quantity?.toString();

    report.SR_CTLineRequired = schematicsData?.miscellaneous?.miscList?.[10]?.isRequired ? 'Yes' : (schematicsData?.miscellaneous?.miscList?.[10]?.isRequired === false ? 'No' : '');
    report.SR_CTLineProvider = schematicsData?.miscellaneous?.miscList?.[10]?.provider?.toString() ?? '';
    report.SR_CTLineType = schematicsData?.miscellaneous?.miscList?.[10]?.type?.toString() ?? '';
    report.SR_CTLineQuantity = (schematicsData?.miscellaneous?.miscList?.[10]?.quantity || 0) === 0 ? '' : schematicsData?.miscellaneous?.miscList?.[10]?.quantity?.toString();
}

const createDepletionChart = (chartData: DepletionCurveGraphData) => {
    let chartDataArray: any[] = []

    chartData.wiggins.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "wiggins"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });

    chartData.depletion10Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion10Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });

    chartData.depletion20Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion20Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });


    chartData.depletion30Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion30Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
        i++
    });

    chartData.depletion40Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion40Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });


    chartData.depletion50Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion50Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });

    chartData.depletion60Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion60Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
        i++
    });

    chartData.depletion70Percent.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "depletion70Percent"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });

    // chartData.generalDepletion.forEach((data, i) => {
    //     var point2 = new chartDataModel()
    //     point2.SeriesName = "generalDepletion"
    //     point2.PointValue = {
    //         IsEmpty: false,
    //         X: data.liquidRate,
    //         Y: data.fbhp
    //     }
    //     point2.PointNo = "Point" + i.toString()
    //     chartDataArray.push(point2)
    // });

    return chartDataArray

}

const createIPRChart = (chartData: IPRplotData) => {
    let chartDataArray: any[] = []

    chartData.wiggins.forEach((data, i) => {
        var point = new chartDataModel()
        point.SeriesName = "Wiggins"
        point.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)
    });

    chartData.vogel.forEach((data, i) => {
        var point2 = new chartDataModel()
        point2.SeriesName = "Vogel"
        point2.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
    });

    chartData.constantPi.forEach((data, i) => {
        var point3 = new chartDataModel()
        point3.SeriesName = "Gilbert"
        point3.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point3.PointNo = "Point" + i.toString()
        chartDataArray.push(point3)
    });

    chartData.compositePi.forEach((data, i) => {
        var point4 = new chartDataModel()
        point4.SeriesName = "Composite"
        point4.PointValue = {
            IsEmpty: false,
            X: data.liquidRate,
            Y: data.fbhp
        }
        point4.PointNo = "Point" + i.toString()
        chartDataArray.push(point4)
    });

    return chartDataArray
}


const GenerateChart1AxisPoints = (chartData: any, report: MainReport) => {
    report.graph1DataMaximum = parseFloat(chartData[3][4])?.toString()
    report.graph1DataMajorStep = parseFloat(chartData[3][3])?.toString()
    report.graph1DataPowerMaximum = parseFloat(chartData[3][9])?.toString()
    report.graph1DataPowerMajorStep = parseFloat(chartData[3][8])?.toString()
    report.graph1DataEfficiencyMaximum = (parseFloat(chartData[3][5]) * 10)?.toString()
    report.graph1DataEfficiencyMajorStep = '10'
    report.graph1DataHeadMaximum = parseFloat(chartData[3][7])?.toString()
    report.graph1DataHeadMajorStep = parseFloat(chartData[3][6])?.toString()
    report.graph1DataHeadAxisTitle = "Head (" + chartData[3][1]?.toString() + ")"
    report.graph1DataPowerAxisTitle = "Power (" + chartData[3][2]?.toString() + ")"
    report.graph1DataCapacityAxisTitle = "Capacity (" + chartData[3][0]?.toString() + ")"
};

const GenerateChart2AxisPoints = (chartData: any, report: MainReport) => {
    report.graph2DataMaximum = parseFloat(chartData[11][3])?.toString()
    report.graph2DataMajorStep = parseFloat(chartData[11][2])?.toString()
    report.graph2DataHeadMaximum = parseFloat(chartData[11][5])?.toString()
    report.graph2DataHeadMajorStep = parseFloat(chartData[11][4])?.toString()
    report.graph2DataHeadAxisTitle = "Head (" + chartData[11][1]?.toString() + ")"
    report.graph2DataCapacityAxisTitle = "Capacity (" + chartData[11][0]?.toString() + ")"
}

const GenerateChart3AxisPoints = (chartData: any, report: MainReport) => {
    report.graph3DataMaximum = parseFloat(chartData[11][3])?.toString()
    report.graph3DataMajorStep = parseFloat(chartData[11][2])?.toString()
    report.graph3DataHeadMaximum = parseFloat(chartData[11][5])?.toString()
    report.graph3DataHeadMajorStep = parseFloat(chartData[11][4])?.toString()
    report.graph3DataPowerAxisTitle = "Power (" + chartData[11][1]?.toString() + ")"
    report.graph3DataCapacityAxisTitle = "Capacity (" + chartData[11][0]?.toString() + ")"
};

const GenerateIPRChartAxisPoints = (chartData: any, report: MainReport) => {
    report.iprGraphDataMaxXAxis = parseFloat(chartData?.x_Plot_MaxValue)?.toString();
    report.iprGraphDataMaxYAxis = parseFloat(chartData?.y_PlotMaxValue)?.toString();
    report.iprGraphDataXAxisInterval = Math.round(parseFloat(chartData?.x_Plot_Interval)).toString();
    report.iprGraphDataYAxisInterval = parseFloat(chartData?.y_Plot_Interval)?.toString();
}

const GenerateDepletionChartAxisPoints = (
    chartData: any,
    report: MainReport
) => {
    report.depletionGraphDataXMax = parseFloat(chartData?.x_Plot_MaxValue)?.toString();
    report.depletionGraphDataYMax = parseFloat(chartData?.y_Plot_MaxValue)?.toString();
    report.depletionGraphDataXAxisInterval = parseFloat(chartData?.x_Plot_Interval)?.toString();
    report.depletionGraphDataYAxisInterval = parseFloat(chartData?.y_Plot_Interval)?.toString();
};

const GenerateChart1Points = (chartData: any) => {
    let chartDataArray: any[] = []

    var head: [] = chartData[0]
    head.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Head"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Power: [] = chartData[1]
    Power.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Power"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Efficiency: [] = chartData[2]
    Efficiency.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Efficiency"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var min: [] = chartData[4]
    var bep: [] = chartData[5]
    var max: [] = chartData[6]
    var TargetH: [] = chartData[8]
    var TargetV: [] = chartData[9]

    for (let i = 0; i <= 1; i++) {

        var point = new chartDataModel()
        point.SeriesName = "Min"
        var _point = min[i]
        point.PointValue = {
            IsEmpty: false,
            X: _point[0],
            Y: _point[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)

        var point2 = new chartDataModel()
        point2.SeriesName = "BEP"
        var _point2 = bep[i]
        point2.PointValue = {
            IsEmpty: false,
            X: _point2[0],
            Y: _point2[1]
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)

        var point3 = new chartDataModel()
        point3.SeriesName = "Max"
        var _point3 = max[i]
        point3.PointValue = {
            IsEmpty: false,
            X: _point3[0],
            Y: _point3[1]
        }
        point3.PointNo = "Point" + i.toString()
        chartDataArray.push(point3)

        var point4 = new chartDataModel()
        point4.SeriesName = "TargetH"
        var _point4 = TargetH[i]
        point4.PointValue = {
            IsEmpty: false,
            X: _point4[0],
            Y: _point4[1]
        }
        point4.PointNo = "Point" + i.toString()
        chartDataArray.push(point4)

        var point5 = new chartDataModel()
        point5.SeriesName = "TargetV"
        var _point5 = TargetV[i]
        point5.PointValue = {
            IsEmpty: false,
            X: _point5[0],
            Y: _point5[1]
        }
        point5.PointNo = "Point" + i.toString()
        chartDataArray.push(point5)
    }
    var i = 0
    var Target: [] = chartData[7]

    var point6 = new chartDataModel()
    point6.SeriesName = "TargetPoint"
    var _point6 = Target[i]
    point6.PointValue = {
        IsEmpty: false,
        X: _point6[0],
        Y: _point6[1]
    }
    chartDataArray.push(point6)

    return chartDataArray
}
const GenerateChart3Points = (chartData: any) => {
    let chartDataArray: any[] = []

    var F45: [] = chartData[0]
    var i = 0

    F45.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "F45"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)
        i++
    });

    var F50: [] = chartData[1]
    var i = 0

    F50.forEach(element => {
        var point2 = new chartDataModel()
        point2.SeriesName = "F50"
        point2.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point2.PointNo = "Point" + i.toString()
        chartDataArray.push(point2)
        i++
    });

    var F55: [] = chartData[2]
    var i = 0

    F55.forEach(element => {
        var point3 = new chartDataModel()
        point3.SeriesName = "F55"
        point3.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point3.PointNo = "Point" + i.toString()
        chartDataArray.push(point3)
        i++
    });


    var F60: [] = chartData[3]
    var i = 0

    F60.forEach(element => {
        var point4 = new chartDataModel()
        point4.SeriesName = "F60"
        point4.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point4.PointNo = "Point" + i.toString()
        chartDataArray.push(point4)
        i++
    });

    var F65: [] = chartData[4]
    var i = 0

    F65.forEach(element => {
        var point5 = new chartDataModel()
        point5.SeriesName = "F65"
        point5.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point5.PointNo = "Point" + i.toString()
        chartDataArray.push(point5)
        i++
    });


    var F70: [] = chartData[5]
    var i = 0

    F70.forEach(element => {
        var point6 = new chartDataModel()
        point6.SeriesName = "F70"
        point6.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point6.PointNo = "Point" + i.toString()
        chartDataArray.push(point6)
        i++
    });

    var F75: [] = chartData[6]
    var i = 0

    F75.forEach(element => {
        var point7 = new chartDataModel()
        point7.SeriesName = "F75"
        point7.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        point7.PointNo = "Point" + i.toString()
        chartDataArray.push(point7)
        i++
    });

    var min: [] = chartData[7]
    var max: [] = chartData[8]
    var bep: [] = chartData[9]
    var Target: [] = chartData[10]

    for (let i = 0; i < 13; i++) {

        var point = new chartDataModel()
        point.SeriesName = "Min"
        var _point = min[i]
        point.PointValue = {
            IsEmpty: false,
            X: _point[0],
            Y: _point[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)

        var point = new chartDataModel()
        point.SeriesName = "Max"
        var _point = max[i]
        point.PointValue = {
            IsEmpty: false,
            X: _point[0],
            Y: _point[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)

        var point = new chartDataModel()
        point.SeriesName = "BEP"
        var _point = bep[i]
        point.PointValue = {
            IsEmpty: false,
            X: _point[0],
            Y: _point[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)

        var point = new chartDataModel()
        point.SeriesName = "Target"
        var _point = Target[i]
        point.PointValue = {
            IsEmpty: false,
            X: _point[0],
            Y: _point[1]
        }
        point.PointNo = "Point" + i.toString()
        chartDataArray.push(point)
    }

    var i = 0
    var TargetPoint: [] = chartData[12]

    var point6 = new chartDataModel()
    point6.SeriesName = "TargetPoint"
    var _point6 = TargetPoint[i]
    point6.PointValue = {
        IsEmpty: false,
        X: _point6[0],
        Y: _point6[1]
    }
    chartDataArray.push(point6)

    return chartDataArray

}

const GenerateClearanceChart1Points = (chartData: any) => {
    let chartDataArray: any[] = []
    var Casing: [] = chartData[0]

    Casing?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Casing"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Tubing: [] = chartData[1]

    Tubing?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Tubing"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Cable: [] = chartData[2]

    Cable?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Cable"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    return chartDataArray

};

const GenerateClearanceChart2Points = (chartData: any) => {
    let chartDataArray: any[] = []
    var Casing: [] = chartData[0]

    Casing?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Casing"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Pump: [] = chartData[3]

    Pump?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Pump"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });


    var MotorLead: [] = chartData[4]

    MotorLead?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "MotorLead"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    return chartDataArray

}

const GenerateClearanceChart3Points = (chartData: any) => {
    let chartDataArray: any[] = []
    var Casing: [] = chartData[0]

    Casing?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Casing"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Protector: [] = chartData[5]

    Protector?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Protector"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });


    var MotorLead: [] = chartData[6]

    MotorLead?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "MotorLead"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    return chartDataArray

}

const GenerateClearanceChart4Points = (chartData: any) => {
    let chartDataArray: any[] = []
    var Casing: [] = chartData[0]

    Casing?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Casing"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    var Motor: [] = chartData[7]

    Motor?.forEach(element => {
        var point = new chartDataModel()
        point.SeriesName = "Motor"
        point.PointValue = {
            IsEmpty: false,
            X: element[0],
            Y: element[1]
        }
        chartDataArray.push(point)
    });

    return chartDataArray

}


export const showToast = (message: string, type: 'success' | 'error' | 'warning' = "success") => {
    const options = {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        style: {
            background: type === "success" ? "#4CAF50" : type === "warning" ? "#dc6803" : "#f44336",
            color: "#fff",
        },
    };

    type === "success" ? toast.success(message, options as any) : type === "warning" ? toast.warning(message, options as any) : toast.error(message, options as any);
};

export function handleSearch(search: string, data: any[], field?: string) {
    const keyword = search.toLowerCase().trim();

    if (!keyword) return data;

    if (field) {
        return data.filter((item: any) => {
            const value = item?.[field];
            return typeof value === "string" && value.toLowerCase().includes(keyword);
        });
    }

    return data.filter((item: any) =>
        Object.values(item).some(
            (val) => typeof val === "string" && val.toLowerCase().includes(keyword)
        )
    );
}

export function updateCaseName(name: string, duplicate: boolean, data: any[]) {
    if (duplicate) {
        // Extract the base name without the version suffix
        const baseMatch = name.match(/^(.*?)(?:\s+V\d+)?$/i);
        const baseName = baseMatch ? baseMatch[1].trim() : name;

        // Find all versions of this base name in the data
        const versions = data
            .map((s: any) => s?.displayCaseName)
            .filter((n: string) => n?.toLowerCase().startsWith(baseName.toLowerCase()))
            .map((n: string) => {
                const vMatch = n.match(/V(\d+)/i);
                return vMatch ? parseInt(vMatch[1], 10) : 1; // Base case without V is treated as V1
            });

        const maxVersion = versions.length > 0 ? Math.max(...versions) : 1;
        return `${baseName} V${maxVersion + 1}`;
    } else {
        const max = Math.max(
            ...data
                .map((s: any) => parseInt(s?.displayCaseName?.match(/#\s*(\d+)/)?.[1] ?? "0", 10))
        );
        return `Case #${max + 1}`;
    }
}

export const convertToSentenceCase = (input: string): string => {
    // Remove "SR" or "DR" prefixes
    if (input.startsWith("SR")) {
        input = input.substring(2);
    } else if (input.startsWith("DR")) {
        input = input.substring(2);
    }

    // Convert camelCase to Sentence Case
    let result = input
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before uppercase letters
        .replace(/^./, (str) => str.toUpperCase()); // Capitalize the first letter

    // Capitalize first letter after "/" symbol
    result = result.replace(/\/\s*([a-zA-Z])/g, (match, p1) => `/${p1.toUpperCase()}`);

    return result;
};


export function toLocalISOString(date: Date): string {
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? "+" : "-";
    const pad = (num: any) => String(Math.floor(Math.abs(num))).padStart(2, "0");

    return (
        date.getFullYear() +
        "-" + pad(date.getMonth() + 1) +
        "-" + pad(date.getDate()) +
        "T" + pad(date.getHours()) +
        ":" + pad(date.getMinutes()) +
        ":" + pad(date.getSeconds()) +
        dif + pad(tzo / 60) + ":" + pad(tzo % 60)
    );
};

export function generateRange(min: number, max: number, step: number): any[] {
    const result = [];
    if (min > 0 && max > 0 && step > 0) {
        for (let i = min; i <= max; i = +(i + step)?.toFixed(2)) {
            result?.push(i);
        };
    };
    return result;
};

export const getJetliftMainReportDataFromUtility = (stateData: RootState) => {
    const { tubingInput, units } = stateData?.jetlift?.tubing;
    const { tab4Input } = stateData?.projectTab;
    const { fluidInput, region, regionList } = stateData?.jetlift?.fluid;
    const { jetPump } = stateData?.jetlift?.jetPump;
    const selectedRegion: any = regionList?.find((x: any) => x?.value === region);
    const { wellInput, inflowOutput } = stateData?.jetlift?.well;
    const projectDetails: any = stateData?.projectTab?.tab4Input;
    const { gasIntakeData, } = stateData?.jetlift?.gasCalculations;
    const { wellInflowData, iprCurve, depletionCurve, showIPRPlotInMainReport, showDepletionPlotInMainReport, basicSummary, formattedBasicSummaryScenarios } = stateData?.jetlift?.basicCalculations;
    const isInValid = (value: string) => {
        return value === undefined || value === null || value === "" || value === "0";
    };
    const {
        accountManagerList,
        accountManager,
        caseName,
        jetliftProjectID
    } = stateData?.createProject;
    let report = new JetLiftMainReport();
    report.ProjectName = projectDetails?.projectName ?? tab4Input?.projectName ?? "";
    report.CaseName = projectDetails?.caseName ?? tab4Input?.caseName ?? "";
    report.CasingOD = tubingInput?.casingOD ? tubingInput?.casingOD?.toString() : "";
    report.CasingID = tubingInput?.casingID ? tubingInput?.casingID?.toString() : "";
    report.CasingWeight = tubingInput?.casingWeight ? tubingInput?.casingWeight?.toString() : "";
    report.CasingOD_Unit = units?.actNameCasingOD ? units?.actNameCasingOD?.toString() : "in";
    report.CasingWeight_Unit = units?.actNameCasingWeight ? units?.actNameCasingWeight?.toString() : "lb/ft";
    report.tubing1_TubingOD = tubingInput?.tubing1_tubingOD ? tubingInput?.tubing1_tubingOD?.toString() : "";
    report.tubing1_TubingID = tubingInput?.tubing1_tubingID ? tubingInput?.tubing1_tubingID?.toString() : "";
    report.tubing1_TubingWeight = tubingInput?.tubing1_tubingWeight ? tubingInput?.tubing1_tubingWeight?.toString() : "";
    report.tubing1_TubingOD_Unit = units?.actNameTubingOD ? units?.actNameTubingOD?.toString() : "in";
    report.tubing1_TubingWeight_Unit = units?.actNameTubingWeight ? units?.actNameTubingWeight?.toString() : "lb/ft";
    report.tubing2_TubingOD = tubingInput?.tubing2_tubingOD ? tubingInput?.tubing2_tubingOD?.toString() : "";
    report.tubing2_TubingID = tubingInput?.tubing2_tubingID ? tubingInput?.tubing2_tubingID?.toString() : "";
    report.Casing = report.CasingOD + ' ' + report.CasingOD_Unit + ' - ' + report.CasingWeight + ' ' + report.CasingWeight_Unit;
    report.Tubing1 = report.tubing1_TubingOD + ' ' + report.tubing1_TubingOD_Unit + ' - ' + report.tubing1_TubingWeight + ' ' + report.tubing1_TubingWeight_Unit;
    report.Tubing2 = report.tubing2_TubingOD + ' ' + report.tubing2_TubingOD_Unit + ' - ' + report.tubing2_TubingWeight + ' ' + report.tubing2_TubingWeight_Unit;
    report.pipeRoughness = tubingInput?.pipeRoughness?.toString() ?? "";
    report.flowLinePressure = wellInput?.casingPressure ? wellInput?.casingPressure?.toString() : "";
    report.productivityIndex = Number(inflowOutput?.productivityIndex).toFixed(2);
    report.staticBHP = Number(inflowOutput?.staticBottomHolePressure).toFixed(2);
    report.flowingBHP = Number(inflowOutput?.flowingBottomHolePressure).toFixed(2);
    report.staticFL = Number(inflowOutput?.staticFluidLevel).toFixed(2);
    report.producingFL = Number(inflowOutput?.producingFluidLevel).toFixed(2);
    report.testFlowRate = wellInput?.inflowTypeLstVal?.toString() === "1" ? (wellInput?.flowRate ? wellInput?.flowRate?.toString() : "") : wellInput?.inflowTypeLstVal?.toString() === "2" ? (wellInput?.flowRateForHd ? wellInput?.flowRateForHd?.toString() : "") : "";
    report.reservoirPressure = wellInput?.inflowTypeLstVal?.toString() === "0" ? (wellInput?.reservoirPressure ? wellInput?.reservoirPressure?.toString() : "") : (wellInput?.sbhpForPressre ? wellInput?.sbhpForPressre?.toString() : "");
    report.staticBottomHolePressurePI = wellInput?.reservoirPressure ? wellInput?.reservoirPressure?.toString() : "";
    report.staticFluidLevelfromSurfacePI = wellInput?.staticFluidLvl ? wellInput?.staticFluidLvl?.toString() : "";
    report.desiredProductionRate = wellInput?.productionRate ? wellInput?.productionRate?.toString() : "";
    report.pumpSuctionPressure = wellInput?.desiredPumpSuctionPressure ? wellInput?.desiredPumpSuctionPressure?.toString() : "";
    report.staticBottomHolePressureP = wellInput?.sbhpForPressre ? wellInput?.sbhpForPressre?.toString() : "";
    report.flowingBottomHolePressureP = wellInput?.intakePressure ? wellInput?.intakePressure?.toString() : "";
    report.testFlowRateP = wellInput?.flowRate ? wellInput?.flowRate?.toString() : "";
    report.staticFuidLevelfromSurfaceHead = wellInput?.staticFluidLvlForHd ? wellInput?.staticFluidLvlForHd?.toString() : "";
    report.producingFluidLevelfromSurfaceHead = wellInput?.prdcngFluidLvl ? wellInput?.prdcngFluidLvl?.toString() : "";
    report.testFlowRateHead = wellInput?.flowRateForHd ? wellInput?.flowRateForHd?.toString() : "";
    report.settingDepth = wellInput?.pumpSetting ? wellInput?.pumpSetting?.toString() : "";
    report.perforation = wellInput?.perforation ? wellInput?.perforation?.toString() : "";
    report.tvd = wellInput?.tvd ? wellInput?.tvd?.toString() : "";
    report.spGrWater = fluidInput?.spGrWater ? fluidInput?.spGrWater?.toString() : "";
    report.waterCut = fluidInput?.waterCut ? fluidInput?.waterCut?.toString() : "";
    report.percentage_unit = units?.actNameWaterCut ? units?.actNameWaterCut?.toString() : "%";
    report.bwpd = fluidInput?.bwpd ? fluidInput?.bwpd?.toString() : "";
    report.oilAPI = fluidInput?.oilAPI ? fluidInput?.oilAPI?.toString() : "";
    report.spGrOil = fluidInput?.spGrOil ? fluidInput?.spGrOil?.toString() : "";
    report.oilCut = fluidInput?.oilCut ? fluidInput?.oilCut?.toString() : "";
    report.bopd = fluidInput?.bopd ? fluidInput?.bopd?.toString() : "";
    report.bpd_unit = units?.actNameBOPD ? units?.actNameBOPD?.toString() : "bpd";
    report.psi_unit = units?.actNameBubblePoint ? units?.actNameBubblePoint?.toString() : "psi";
    report.ft_unit = units?.actNamePumpSettingVertical ? units?.actNamePumpSettingVertical?.toString() : "ft";
    report.temp_unit = units?.actNameBottomHoleTemp ? units?.actNameBottomHoleTemp?.toString() : "°F";
    report.averageCombinedSpGr = fluidInput?.averageCombinedSpGr ? fluidInput?.averageCombinedSpGr?.toString() : "";
    report.liquidPhaseCombinedSpGr = fluidInput?.liquidPhaseCombinedSpGr ? fluidInput?.liquidPhaseCombinedSpGr?.toString() : "";
    report.compositeSpGr = fluidInput?.compositeSpGr ? fluidInput?.compositeSpGr?.toString() : "";
    report.fluidViscosity = fluidInput?.fluidViscosity ? fluidInput?.fluidViscosity?.toString() : "";
    report.gasAvailblty = fluidInput?.gasAvailblty ? fluidInput?.gasAvailblty?.toString() : "";
    report.gasProduction = fluidInput?.gasProduction ? fluidInput?.gasProduction?.toString() : "";
    report.gasProduction_unit = units?.actNameGasProduction ? units?.actNameGasProduction?.toString() : "mcf";
    report.gor = fluidInput?.gor ? fluidInput?.gor?.toString() : "";
    report.gor_unit = units?.actNameGOR ? units?.actNameGOR?.toString() : "scf/stb";
    report.glr = fluidInput?.glr ? fluidInput?.glr?.toString() : "";
    report.glr_unit = units?.actNameGLR ? units?.actNameGLR?.toString() : "scf/stb";
    report.specificGravityGas = fluidInput?.spGrGas ? fluidInput?.spGrGas?.toString() : "";
    report.gasCompressibility = fluidInput?.gasCompressibility ? fluidInput?.gasCompressibility?.toString() : "";
    report.bubblePointPressure = fluidInput?.bubblePoint ? fluidInput?.bubblePoint?.toString() : "";
    report.bottomHoleTemperature = fluidInput?.bottomHoleTemp ? fluidInput?.bottomHoleTemp?.toString() : "";
    report.surfaceTemperature = fluidInput?.surfaceTemperature ? fluidInput?.surfaceTemperature?.toString() : "";
    report.region = selectedRegion?.label?.toString() ?? "";
    report.manufacturer = jetPump?.manufacturer ? jetPump?.manufacturer?.toString() : "";
    report.nozzleDiameterSize = jetPump?.nozzleDiameterSize ? jetPump?.nozzleDiameterSize?.toString() : "";
    report.nozzleAreaSize = jetPump?.nozzleAreaSize ? jetPump?.nozzleAreaSize?.toString() : "";
    report.nozzleValue = jetPump?.nozzleValue ? jetPump?.nozzleValue?.toString() : "";
    report.in_unit = units?.actNameCasingID ? units?.actNameCasingID?.toString() : "in";
    report.throatDiameterSize = jetPump?.throatDiameterSize ? jetPump?.throatDiameterSize?.toString() : "";
    report.throatAreaSize = jetPump?.throatAreaSize ? jetPump?.throatAreaSize?.toString() : "";
    report.throatValue = jetPump?.throatValue ? jetPump?.throatValue?.toString() : "";
    report.powerFluidType = jetPump?.powerFluidType ? jetPump?.powerFluidType?.toString() : "";
    report.powerFluidOilWater = jetPump?.powerFluidType?.toString() === "1" ? "Water" : "Oil";
    report.powerFluidDensity = jetPump?.powerFluidType === "1" ? fluidInput?.spGrWater?.toString() : jetPump?.powerFluidType === "2" ? fluidInput?.oilAPI?.toString() : fluidInput?.spGrWater?.toString();
    report.minFluidRange = jetPump?.minFluidRange ? jetPump?.minFluidRange?.toString() : "";
    report.maxFluidRange = jetPump?.maxFluidRange ? jetPump?.maxFluidRange?.toString() : "";
    report.stepFluidRange = jetPump?.stepFluidRange ? jetPump?.stepFluidRange?.toString() : "";
    report.outputPressures = jetPump?.outputPressures ? jetPump?.outputPressures : "";
    report.recommended_vessel_size = jetPump?.recommended_vessel_size ? jetPump?.recommended_vessel_size : "";
    report.recommended_charge_pump_size = jetPump?.recommended_charge_pump_size ? jetPump?.recommended_charge_pump_size : "";
    report.recommended_surface_pump_size = jetPump?.recommended_surface_pump_size ? jetPump?.recommended_surface_pump_size : "";
    report.recommended_plunget_size = jetPump?.recommended_plunget_size ? jetPump?.recommended_plunget_size : "";
    report.selected_vessel_size = jetPump?.selected_vessel_size ? jetPump?.selected_vessel_size : "";
    report.selected_charge_pump_size = jetPump?.selected_charge_pump_size ? jetPump?.selected_charge_pump_size : "";
    report.selected_surface_pump_size = jetPump?.selected_surface_pump_size ? jetPump?.selected_surface_pump_size : "";
    report.selected_plunget_size = jetPump?.selected_plunget_size ? jetPump?.selected_plunget_size : "";
    report.schematics_jet_pump_size = jetPump?.schematics_jet_pump_size ? jetPump?.schematics_jet_pump_size : "";
    report.pumpInstallation = "Casing installation";
    report.gasVent = jetPump?.gasVent ? jetPump?.gasVent?.toString() : "";
    report.Gas_Intake_bg = gasIntakeData?.bg ? gasIntakeData?.bg?.toString() : "";
    report.Gas_Intake_bo = gasIntakeData?.bo ? gasIntakeData?.bo?.toString() : "";
    report.Gas_Intake_rs = gasIntakeData?.rs ? gasIntakeData?.rs?.toString() : "";
    report.Gas_Intake_gasVolume = gasIntakeData?.gasVolume ? gasIntakeData?.gasVolume?.toString() : "";
    report.Gas_Intake_oilVolume = gasIntakeData?.oilVolume ? gasIntakeData?.oilVolume?.toString() : "";
    report.Gas_Intake_waterVolume = gasIntakeData?.waterVolume ? gasIntakeData?.waterVolume?.toString() : "";
    report.Gas_Intake_tfv = gasIntakeData?.tfv ? gasIntakeData?.tfv?.toString() : "";
    report.Gas_Intake_freeGas = gasIntakeData?.freeGas ? gasIntakeData?.freeGas?.toString() : "";
    report.Gas_Intake_gor = gasIntakeData?.gor ? gasIntakeData?.gor?.toString() : "";
    report.Gas_Intake_glr = gasIntakeData?.glr ? gasIntakeData?.glr?.toString() : "";
    report.Gas_Intake_liquidPhaseDensity = gasIntakeData?.liquidPhaseDensity ? gasIntakeData?.liquidPhaseDensity?.toString() : "";
    report.Gas_Intake_gasCompressibility = gasIntakeData?.gasCompressibility ? gasIntakeData?.gasCompressibility?.toString() : "";
    report.Gas_Intake_pip = gasIntakeData?.pip ? gasIntakeData?.pip?.toString() : "";
    report.Gas_Intake_compositeSpGr = gasIntakeData?.compositeSpGr ? gasIntakeData?.compositeSpGr?.toString() : "";
    report.Gas_Intake_flop = gasIntakeData?.flop ? gasIntakeData?.flop?.toString() : "";
    report.Gas_Intake_liquidPhaseSpGr = gasIntakeData?.liquidPhaseSpGr ? gasIntakeData?.liquidPhaseSpGr?.toString() : "";
    report.Gas_Intake_temperatureOut = gasIntakeData?.temperatureOut ? gasIntakeData?.temperatureOut?.toString() : "";
    report.ShowIPRPlotOnMainReport = showIPRPlotInMainReport;
    report.ShowDepletionPlotOnMainReport = showDepletionPlotInMainReport;
    report.surfacePowerFluidInjectionPressure = basicSummary?.surfacePowerFluidInjectionPressure !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionPressure)?.toFixed(2)?.toString() : "";
    report.surfacePowerFluidInjectionRate = basicSummary?.surfacePowerFluidInjectionRate !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionRate)?.toFixed(2)?.toString() : "";
    report.pumpIntakePressure = basicSummary?.pumpIntakePressure !== "NaN" ? Number(basicSummary?.pumpIntakePressure)?.toFixed(2)?.toString() : "";
    report.pumpDischargePressure = basicSummary?.pumpDischargePressure !== "NaN" ? Number(basicSummary?.pumpDischargePressure)?.toFixed(2)?.toString() : "";
    report.powerFluidPressureAtPump = basicSummary?.powerFluidPressureAtPump !== "NaN" ? Number(basicSummary?.powerFluidPressureAtPump)?.toFixed(2)?.toString() : "";
    report.horsePowerRequirement = basicSummary?.horsePowerRequirement !== "NaN" ? Number(basicSummary?.horsePowerRequirement)?.toFixed(2)?.toString() : "";
    report.formattedBasicSummaryScenarios = formattedBasicSummaryScenarios ? formattedBasicSummaryScenarios : [];

    if (showIPRPlotInMainReport && iprCurve) {
        report.IPRGraphData = JSON.stringify(createIPRChart(iprCurve));
        GenerateIPRChartAxisPoints(iprCurve, report)
    };
    if (showDepletionPlotInMainReport && depletionCurve) {
        report.DepletionGraphData = JSON.stringify(createDepletionChart(depletionCurve));
        GenerateDepletionChartAxisPoints(depletionCurve, report)
    };

    let mainReportPayload = {
        inputs: report,
        projectId: jetliftProjectID?.projectID?.toString(),
        unitSystem: "0",
    };

    return mainReportPayload;
};

export const reverseTransform = (data: any[]) => {
    if (data && data?.length > 0) {
        return data?.reduce((acc, item) => {
            acc[String(item.label)] = item?.value?.toString();
            return acc;
        }, {});
    }
    return {};
};


export const getJLReportDataFromUtility = (stateData: RootState) => {
    const { tubingInput } = stateData?.jetlift?.tubing ?? {};
    const { tab4Input } = stateData?.projectTab ?? {};
    const { fluidInput } = stateData?.jetlift?.fluid ?? {};
    const { jetPump } = stateData?.jetlift?.jetPump ?? {};
    const { wellInput } = stateData?.jetlift?.well ?? {};
    const { basicSummary, basicSummaryScenarios, iprCurve, showIPRPlotInMainReport } = stateData?.jetlift?.basicCalculations ?? {};
    const { gasIntakeData } = stateData?.jetlift?.gasCalculations ?? {};
    const projectDetails: any = stateData?.projectTab?.tab4Input ?? tab4Input;
    const { jetliftProjectID, caseName: createCaseName } = stateData?.createProject ?? {};
    const { userRole } = stateData?.auth?.user ?? {};

    const toStr = (v: any) => (v != null && v !== "") ? String(v) : "";

    const wellStaticBHP = wellInput?.inflowTypeLstVal === "0" ? wellInput?.reservoirPressure : wellInput?.inflowTypeLstVal === "1" ? wellInput?.sbhpForPressre : wellInput?.reservoirPressure;
    const wellTestFlowRate = wellInput?.inflowTypeLstVal === "1" ? wellInput?.flowRate : wellInput?.inflowTypeLstVal === "2" ? wellInput?.flowRateForHd : wellInput?.flowRate;
    const powerFluidOilWater = jetPump?.powerFluidType === "1" ? "Water" : jetPump?.powerFluidType === "2" ? "Oil" : toStr(jetPump?.powerFluidType);

    const scenarios = Array.isArray(basicSummaryScenarios) ? basicSummaryScenarios : [];
    // Normalize scenario item: API may return matchProdRate/matchPumpIntakePres (chart shape) or matchProductionRateFromGraph/matchIntakeFromGraph (summary shape)
    const scenarioVal = (s: any, keys: string[]) => {
        if (!s) return undefined;
        for (const k of keys) {
            const v = s[k];
            if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v);
        }
        return undefined;
    };
    const getProdRate = (s: any) => scenarioVal(s, ["matchProductionRateFromGraph", "matchProdRate"]);
    const getPFP = (s: any) => scenarioVal(s, ["matchPowerFluidRate", "matchPwrFluidPress"]);
    const getQn = (s: any) => scenarioVal(s, ["matchQn", "matchPwrFluidRate"]);
    const getPIP = (s: any) => scenarioVal(s, ["matchIntakeFromGraph", "matchPumpIntakePres"]);
    const getDischarge = (s: any) => scenarioVal(s, ["discharge", "pumpDischargePrs"]);
    const getPn = (s: any) => scenarioVal(s, ["matchPn", "matchPwrFldPrsAtPmp"]);

    const s0 = scenarios[0];
    const s1 = scenarios[1];
    const s2 = scenarios[2];
    const s3 = scenarios[3];

    const fmt = (v: number | null | undefined | string) =>
        v != null && v !== "NaN" && !Number.isNaN(Number(v)) ? String(Number(v).toFixed(2)) : "";
    const fmtBlpd = (v: number | null | undefined) => (v != null && !Number.isNaN(v) ? String(v.toFixed(2)) : "");
    const fmtDischarge = (v: number | string | null | undefined) =>
        v != null && v !== "NaN" && !Number.isNaN(Number(v)) ? String(Number(v).toFixed(2)) : "";

    const performanceCurvePoints = scenarios
        .map((s: any) => {
            const flowRate = getProdRate(s);
            const pressure = getPIP(s);
            return flowRate != null && pressure != null ? { flowRate, pressure } : null;
        })
        .filter((p: { flowRate: number; pressure: number } | null): p is { flowRate: number; pressure: number } => p != null);
    const performanceCurveData = performanceCurvePoints.length > 0 ? JSON.stringify(performanceCurvePoints) : "";

    const showIPRPlotOnMainReport = showIPRPlotInMainReport ?? false;
    const iprGraphData = showIPRPlotOnMainReport && iprCurve ? JSON.stringify(createIPRChart(iprCurve)) : "";
    const iprGraphDataMaxXAxis = iprCurve != null ? String(parseFloat(String(iprCurve?.x_Plot_MaxValue ?? ""))) : "";
    const iprGraphDataMaxYAxis = iprCurve != null ? String(parseFloat(String(iprCurve?.y_PlotMaxValue ?? ""))) : "";
    const iprGraphDataXAxisInterval = iprCurve != null ? String(Math.round(parseFloat(String(iprCurve?.x_Plot_Interval ?? "")))) : "";
    const iprGraphDataYAxisInterval = iprCurve != null ? String(parseFloat(String(iprCurve?.y_Plot_Interval ?? ""))) : "";

    const now = new Date().toISOString();
    const today = new Date().toISOString().slice(0, 10);
    const affiliations = stateData?.projectHeader?.projectAndAffiliations;
    const selectedAffiliation = Array.isArray(affiliations)
    ? affiliations.find((a: any) => a?.projectID != null && String(a.projectID) === String(jetliftProjectID?.projectID))
    : undefined;

    return {
        projectId: jetliftProjectID?.projectID != null ? String(jetliftProjectID.projectID) : "",
        documentName: (projectDetails?.caseName ?? createCaseName ?? "")?.toString() ?? "",
        customerName: (tab4Input?.companyName ?? "")?.toString() ?? "",
        wellName: (tab4Input?.wellName ?? "")?.toString() ?? "",
        projectName: (tab4Input?.projectName ?? "")?.toString() ?? "",
        perforationDepth: toStr(wellInput?.perforation),
        pumpVerticalDepth: toStr(wellInput?.tvd),
        pumpInstallation: "Casing installation",
        casingProductionID: toStr(tubingInput?.casingID),
        powerTubingID: toStr(tubingInput?.tubing1_tubingID ?? tubingInput?.tubing2_tubingID),
        powerTubingOD: toStr(tubingInput?.tubing1_tubingOD ?? tubingInput?.tubing2_tubingOD),
        tubingLength: toStr(wellInput?.pumpSetting),
        pipeRoughness_ed_Ld: toStr(tubingInput?.pipeRoughness),
        oilGravity: toStr(fluidInput?.oilAPI),
        producedVolWaterCut: toStr(fluidInput?.waterCut),
        waterSpecificGravity: toStr(fluidInput?.spGrWater),
        producingGOR_scfstb: toStr(fluidInput?.gor),
        gasSpecificGravity: toStr(fluidInput?.spGrGas),
        wellStaticBHP: toStr(wellStaticBHP),
        pumpIntakePress: basicSummary?.pumpIntakePressure != null && basicSummary?.pumpIntakePressure !== "NaN" ? String(basicSummary?.pumpIntakePressure) : "",
        wellTestFlowRate: toStr(wellTestFlowRate),
        wellHeadTemp: toStr(fluidInput?.surfaceTemperature),
        bottomHoleTemp: toStr(fluidInput?.bottomHoleTemp),
        gasVented: toStr(jetPump?.gasVent),
        powerFluidOilWater,
        powerFluidAPI: jetPump?.powerFluidType?.toString() === "2" ? toStr(fluidInput?.oilAPI) : toStr(fluidInput?.spGrWater),
        bubblePointPress: toStr(fluidInput?.bubblePoint),
        wellHeadPress: toStr(wellInput?.casingPressure),
        reservoirPressure: wellInput?.inflowTypeLstVal?.toString() === "0" ? toStr(wellInput?.reservoirPressure) : toStr(wellInput?.sbhpForPressre),
        waterCut_fraction: toStr((Number(fluidInput?.waterCut)/100)?.toFixed(2)),
        flowRate: toStr(wellInput?.flowRate),
        flowingBottomHolePressure: toStr(wellInput?.intakePressure),
        overriddenBubblePointPressure: toStr(fluidInput?.bubblePoint),
        targetProductionRate_blpd: toStr(wellInput?.productionRate),
        targetPumpIntakePressure: basicSummary?.pumpIntakePressure != null && basicSummary?.pumpIntakePressure !== "NaN" ? String(Number(basicSummary?.pumpIntakePressure)?.toFixed(2)) : "",
        predSurfacePowerFluidInjectionPressure: basicSummary?.surfacePowerFluidInjectionPressure != null && basicSummary?.surfacePowerFluidInjectionPressure !== "NaN" ? String(Number(basicSummary?.surfacePowerFluidInjectionPressure)?.toFixed(2)) : "",
        predSurfacePowerFluidInjectionRate: basicSummary?.surfacePowerFluidInjectionRate != null && basicSummary?.surfacePowerFluidInjectionRate !== "NaN" ? String(Number(basicSummary?.surfacePowerFluidInjectionRate)?.toFixed(2)) : "",
        predPumpIntakePressure: basicSummary?.pumpIntakePressure != null && basicSummary?.pumpIntakePressure !== "NaN" ? String(Number(basicSummary?.pumpIntakePressure)?.toFixed(2)) : "",
        predPumpDischargePressure: basicSummary?.pumpDischargePressure != null && basicSummary?.pumpDischargePressure !== "NaN" ? String(Number(basicSummary?.pumpDischargePressure)?.toFixed(2)) : "",
        predPowerFluidPressureAtPumpDepth: basicSummary?.powerFluidPressureAtPump != null && basicSummary?.powerFluidPressureAtPump !== "NaN" ? String(Number(basicSummary?.powerFluidPressureAtPump)?.toFixed(2)) : "",
        predHorsepowerRequirement: basicSummary?.horsePowerRequirement != null && basicSummary?.horsePowerRequirement !== "NaN" ? String(Number(basicSummary?.horsePowerRequirement)?.toFixed(2)) : "",
        matchProdRate1_Blpd: fmtBlpd(getProdRate(s0)),
        matchPowerFluidRate1_QN: fmt(getQn(s0)),
        matchPowerFluidPress1_PFP: fmt(getPFP(s0)),
        matchPumpIntakePress1_PIP: fmtBlpd(getPIP(s0)),
        matchPumpDischargePress1_PD: fmtDischarge(getDischarge(s0)),
        matchPowerFluidPress1_PN: fmt(getPn(s0)),
        matchProdRate2_Blpd: fmtBlpd(getProdRate(s1)),
        matchPowerFluidRate2_QN: fmt(getQn(s1)),
        matchPowerFluidPress2_PFP: fmt(getPFP(s1)),
        matchPumpIntakePress2_PIP: fmtBlpd(getPIP(s1)),
        matchPumpDischargePress2_PD: fmtDischarge(getDischarge(s1)),
        matchPowerFluidPress2_PN: fmt(getPn(s1)),
        matchProdRate3_Blpd: fmtBlpd(getProdRate(s2)),
        matchPowerFluidRate3_QN: fmt(getQn(s2)),
        matchPowerFluidPress3_PFP: fmt(getPFP(s2)),
        matchPumpIntakePress3_PIP: fmtBlpd(getPIP(s2)),
        matchPumpDischargePress3_PD: fmtDischarge(getDischarge(s2)),
        matchPowerFluidPress3_PN: fmt(getPn(s2)),
        matchProdRate4_Blpd: fmtBlpd(getProdRate(s3)),
        matchPowerFluidRate4_QN: fmt(getQn(s3)),
        matchPowerFluidPress4_PFP: fmt(getPFP(s3)),
        matchPumpIntakePress4_PIP: fmtBlpd(getPIP(s3)),
        matchPumpDischargePress4_PD: fmtDischarge(getDischarge(s3)),
        matchPowerFluidPress4_PN: fmt(getPn(s3)),
        performanceCurveData,
        createdBy: toStr(stateData?.auth?.user?.id ?? ""),
        createdOn: now,
        updatedBy: toStr(stateData?.auth?.user?.id ?? ""),
        updatedOn: now,
        unitSystem: tubingInput?.unitSys != null ? Number(tubingInput.unitSys) : 0,
        customerEmail: toStr(selectedAffiliation?.createdByEmail ?? ""),
        customerRole: toStr(userRole ?? ""),
        reportFormat: 0,
        injectionPressureMinRange: toStr(jetPump?.minFluidRange),
        injectionPressureMaxRange: toStr(jetPump?.maxFluidRange),
        nozzleDiameter: toStr(jetPump?.nozzleDiameterSize),
        throatDiameter: toStr(jetPump?.throatDiameterSize),
        pi_Tangent: toStr(wellInput?.piVal),
        pi_Darcey: toStr(wellInput?.piVal),
        downloadedBy: toStr(selectedAffiliation?.createdByName ?? ""),
        downloadedDate: today,
        ShowIPRPlotOnMainReport: showIPRPlotOnMainReport,
        IPRGraphData: iprGraphData,
        iprGraphDataMaxXAxis,
        iprGraphDataMaxYAxis,
        iprGraphDataXAxisInterval,
        iprGraphDataYAxisInterval,
    };
}